'use client'

import {useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode} from 'react'
import {useRouter, useSearchParams} from '@/lib/router'
import {
    Aperture,
    ArrowLeft,
    Copy,
    Download,
    FileText,
    Gift,
    Heart,
    Image as ImageIcon,
    Loader2,
    MessageSquare,
    PanelRightClose,
    PanelRightOpen,
    Smile,
    Search,
    Sparkles,
    TextSearch,
    UserPlus,
    Users,
    X,
} from 'lucide-react'
import type {Account, ChatMessage, Contact, ContactLabel, ReplyPayload} from '@/types'
import type {AccountItem} from '@/hooks/useAccounts'
import {parseContactLabelIds} from '@/shared'
import {apiBlob, apiDelete, apiGet, apiPost} from '@/lib/api'
import {getAuthToken} from '@/lib/api'
import {
    avatarDisplaySrc,
    contactLatestAvatarUrl,
    displayName,
    formatMessageTime,
    getAnyNumber,
} from '@/lib/utils'
import {emptyReplyPayload, payloadText} from '@/lib/utils'
import {ContactProfilePopover} from '@/components/ContactProfilePopover'
import {ImagePreviewOverlay} from '@/components/ImagePreviewOverlay'
import {VideoPlayerOverlay} from '@/components/VideoPlayerOverlay'
import {SexIcon} from '@/components/SexIcon'
import {replaceWeChatEmoticons} from '@/lib/wechat-emoticons'
import { message, message as toast } from '@/components/Toast'
import Markdown from 'react-markdown'
import {ReplyPayloadEditor} from '@/components/ReplyPayloadEditor'
import {VoiceMessageBubble} from '@/components/conversation/VoiceMessageBubble'
import {ConfirmDialog} from '@/components/ConfirmDialog'
import {APP_MESSAGE_EVENT} from '@/hooks/useMessageNotifications'
import {encodeReplyVoicePartForWechat} from '@/lib/voice-codec'
import {CONVERSATION_PIN_CHANGE_EVENT} from '@/lib/conversation-events'

type RenderedMessageKind = 'text' | 'voice' | 'image' | 'emoji' | 'video' | 'file' | 'red-packet'

type ConversationMomentMedia = {type: 'image' | 'video'; url: string; thumbUrl?: string; key?: string; thumbKey?: string}
type ConversationMomentComment = {author: string; content: string; commentId?: number}
type ConversationMomentLike = {author: string; wxid?: string}
type ConversationMoment = {
    momentId: string
    authorWxid: string
    authorName: string
    content: string
    media: ConversationMomentMedia[]
    comments: ConversationMomentComment[]
    likedBy: ConversationMomentLike[]
    createdAt: number
    liked: boolean
    likeCount: number
}

function LazyRender({
    children,
    placeholder,
    rootMargin = '360px',
}: {
    children: ReactNode
    placeholder: ReactNode
    rootMargin?: string
}) {
    const ref = useRef<HTMLDivElement | null>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        if (visible) return
        const node = ref.current
        if (!node) return
        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true)
            return
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return
                setVisible(true)
                observer.disconnect()
            },
            {rootMargin},
        )
        observer.observe(node)
        return () => observer.disconnect()
    }, [rootMargin, visible])

    return (
        <div ref={ref}>
            {visible ? children : placeholder}
        </div>
    )
}

function EmojiMessageImage({messageId, accountWxid}: {messageId: number; accountWxid: string}) {
    const [src, setSrc] = useState('')
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (messageId <= 0 || !accountWxid) return
        let cancelled = false
        let objectURL = ''
        void apiBlob(`/messages/emoji?id=${messageId}&wxid=${encodeURIComponent(accountWxid)}`, {silent: true})
            .then((blob) => {
                if (cancelled) return
                objectURL = URL.createObjectURL(blob)
                setSrc(objectURL)
            })
            .catch(() => {
                if (!cancelled) setFailed(true)
            })
        return () => {
            cancelled = true
            if (objectURL) URL.revokeObjectURL(objectURL)
        }
    }, [accountWxid, messageId])

    if (failed) {
        return (
            <div className="flex items-center gap-2 bg-white border border-hair rounded-md p-3 min-w-[120px]">
                <Smile size={24} className="text-muted"/>
                <span className="text-xs text-muted">表情加载失败</span>
            </div>
        )
    }
    if (!src) {
        return <div className="w-20 h-20 rounded-md bg-gray-100 animate-pulse"/>
    }
    return (
        <img
            src={src}
            alt="表情消息"
            className="block max-w-[160px] max-h-[160px] w-auto h-auto object-contain"
        />
    )
}

function formatMomentTime(timestamp: number): string {
    if (!timestamp) return ''
    const diff = Date.now() - timestamp
    if (diff < 60_000) return '刚刚'
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`
    return new Date(timestamp).toLocaleString([], {month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})
}

function formatMemberTime(timestamp: number): string {
    if (!timestamp) return '-'
    return new Date(timestamp).toLocaleString([], {month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})
}

function memberJoinTime(member: Contact): number {
    const raw = member.raw
    if (!raw || typeof raw !== 'object') return 0
    const record = raw as Record<string, unknown>
    const value = firstRawNumber(record, [
        'JoinTime',
        'joinTime',
        'join_time',
        'CreateTime',
        'createTime',
        'create_time',
        'MemberCreateTime',
        'memberCreateTime',
        'member_create_time',
        'ChatRoomMemberCreateTime',
        'chatRoomMemberCreateTime',
    ])
    if (!value) return 0
    return value < 10_000_000_000 ? value * 1000 : value
}

function firstRawNumber(record: Record<string, unknown>, keys: string[]): number {
    for (const key of keys) {
        const value = record[key]
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
        if (typeof value === 'string') {
            const parsed = Number(value.trim())
            if (Number.isFinite(parsed) && parsed > 0) return parsed
        }
        if (value && typeof value === 'object') {
            const nested = value as Record<string, unknown>
            const nestedValue = nested.value ?? nested.Value ?? nested.str ?? nested.Str
            if (typeof nestedValue === 'number' && Number.isFinite(nestedValue) && nestedValue > 0) return nestedValue
            if (typeof nestedValue === 'string') {
                const parsed = Number(nestedValue.trim())
                if (Number.isFinite(parsed) && parsed > 0) return parsed
            }
        }
    }
    return 0
}

function momentMediaUrl(accountWxid: string, source?: string, key?: string): string {
    if (!accountWxid || !source) return ''
    const params = new URLSearchParams({wxid: accountWxid, url: source})
    if (key) params.set('key', key)
    const token = getAuthToken()
    if (token) params.set('access_token', token)
    return `/api/moments/media?${params.toString()}`
}

function parseJSONList<T>(value: unknown): T[] {
    if (Array.isArray(value)) return value as T[]
    if (typeof value !== 'string' || !value.trim()) return []
    try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed as T[] : []
    } catch {
        return []
    }
}

function objectFrom(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {}
}

function firstValue(source: Record<string, unknown>, ...keys: string[]): unknown {
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key]
    }
    return undefined
}

function numberValue(value: unknown): number {
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : 0
    }
    return 0
}

function timestampMs(value: unknown): number {
    const ts = numberValue(value)
    return ts > 0 && ts < 100_000_000_000 ? ts * 1000 : ts
}

function normalizeMoment(raw: Record<string, unknown>): ConversationMoment {
    const media = parseJSONList<ConversationMomentMedia>(
        raw.media ?? raw.Media ?? raw.media_json ?? raw.MediaJSON,
    )
    const comments = parseJSONList<ConversationMomentComment>(
        raw.comments ?? raw.Comments ?? raw.comments_json ?? raw.CommentsJSON,
    )
    const likedBy = parseJSONList<ConversationMomentLike>(
        raw.likedBy ?? raw.LikedBy ?? raw.likeUserList ?? raw.LikeUserList,
    )
    return {
        momentId: String(raw.momentId ?? raw.MomentID ?? raw.moment_id ?? ''),
        authorWxid: String(raw.authorWxid ?? raw.AuthorWxid ?? raw.author_wxid ?? ''),
        authorName: String(raw.authorName ?? raw.AuthorName ?? raw.author_name ?? ''),
        content: String(raw.content ?? raw.Content ?? ''),
        media,
        comments,
        likedBy,
        createdAt: Number(raw.createdAt ?? raw.CreatedAt ?? raw.created_at ?? 0),
        liked: Boolean(raw.liked ?? raw.Liked),
        likeCount: Number(raw.likeCount ?? raw.LikeCount ?? raw.like_count ?? 0),
    }
}

function normalizeProtocolMoment(raw: Record<string, unknown>): ConversationMoment {
    const objectInfo = objectFrom(firstValue(raw, 'objectInfo', 'ObjectInfo'))
    const contentObject = objectFrom(firstValue(objectInfo, 'ContentObject'))
    const mediaList = objectFrom(firstValue(contentObject, 'MediaList'))
    const mediaRows = firstValue(mediaList, 'Media')
    const media = (Array.isArray(mediaRows) ? mediaRows : [])
        .map((item): ConversationMomentMedia | null => {
            const row = objectFrom(item)
            const url = objectFrom(firstValue(row, 'URL', 'Url'))
            const thumb = objectFrom(firstValue(row, 'Thumb'))
            const enc = objectFrom(firstValue(row, 'Enc'))
            const type = numberValue(firstValue(row, 'Type'))
            const source = String(firstValue(url, 'Value') || '')
            const thumbUrl = String(firstValue(thumb, 'Value') || '')
            const urlKey = String(firstValue(enc, 'Key') || firstValue(url, 'Key', 'VideoMD5', 'MD5') || '')
            const thumbKey = String(firstValue(thumb, 'Key', 'MD5') || urlKey)
            if (!source && !thumbUrl) return null
            return {
                type: type === 6 ? 'video' : 'image',
                url: source || thumbUrl,
                thumbUrl: thumbUrl || source,
                key: urlKey,
                thumbKey,
            }
        })
        .filter((item): item is ConversationMomentMedia => Boolean(item))
    const commentRows = firstValue(raw, 'commentUserList', 'CommentUserList')
    const comments = (Array.isArray(commentRows) ? commentRows : [])
        .map((item): ConversationMomentComment | null => {
            const row = objectFrom(item)
            const content = String(firstValue(row, 'content', 'Content') || '')
            if (!content) return null
            return {
                author: String(firstValue(row, 'nickname', 'Nickname', 'username', 'Username') || ''),
                content,
                commentId: numberValue(firstValue(row, 'commentId', 'CommentId')),
            }
        })
        .filter((item): item is ConversationMomentComment => Boolean(item))
    const likeRows = firstValue(raw, 'likeUserList', 'LikeUserList')
    const likedBy = (Array.isArray(likeRows) ? likeRows : [])
        .map((item): ConversationMomentLike | null => {
            const row = objectFrom(item)
            const author = String(firstValue(row, 'nickname', 'Nickname', 'username', 'Username') || '')
            if (!author) return null
            return {
                author,
                wxid: String(firstValue(row, 'username', 'Username') || ''),
            }
        })
        .filter((item): item is ConversationMomentLike => Boolean(item))
    return {
        momentId: String(firstValue(raw, 'id', 'ID') || firstValue(objectInfo, 'ID') || ''),
        authorWxid: String(firstValue(raw, 'username', 'Username') || firstValue(objectInfo, 'UserName') || ''),
        authorName: String(firstValue(raw, 'nickname', 'Nickname') || ''),
        content: String(firstValue(objectInfo, 'ContentDesc') || firstValue(raw, 'contentDesc', 'ContentDesc') || ''),
        media,
        comments,
        likedBy,
        createdAt: timestampMs(firstValue(raw, 'createTime', 'CreateTime') || firstValue(objectInfo, 'CreateTime')),
        liked: numberValue(firstValue(raw, 'likeFlag', 'LikeFlag')) !== 0,
        likeCount: numberValue(firstValue(raw, 'likeCount', 'LikeCount')),
    }
}

function extractProtocolMoments(response: unknown): ConversationMoment[] {
    const root = objectFrom(response)
    const data = objectFrom(firstValue(root, 'Data', 'data'))
    const objectList = firstValue(data, 'objectList', 'ObjectList')
    if (!Array.isArray(objectList)) return []
    return objectList.map((item) => normalizeProtocolMoment(objectFrom(item)))
}

function ImageMessageImage({messageId, accountWxid, localSrc, onPreview}: {
    messageId: number
    accountWxid: string
    localSrc: string
    onPreview: (src: string) => void
}) {
    const [src, setSrc] = useState(localSrc)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (messageId <= 0 || !accountWxid) {
            setSrc(localSrc)
            return
        }
        let cancelled = false
        let objectURL = ''
        void apiBlob(`/messages/image?id=${messageId}&wxid=${encodeURIComponent(accountWxid)}`, {silent: true})
            .then((blob) => {
                if (cancelled) return
                objectURL = URL.createObjectURL(blob)
                setSrc(objectURL)
                setFailed(false)
            })
            .catch(() => {
                if (!cancelled && !localSrc) setFailed(true)
            })
        return () => {
            cancelled = true
            if (objectURL) URL.revokeObjectURL(objectURL)
        }
    }, [accountWxid, localSrc, messageId])

    if (failed) {
        return (
            <div className="flex items-center gap-3 bg-white border border-hair rounded-md p-3 min-w-[180px] max-w-[250px]">
                <ImageIcon size={32} className="text-muted"/>
                <span className="text-xs text-muted">图片加载失败</span>
            </div>
        )
    }
    if (!src) return <div className="w-40 h-28 rounded-md bg-gray-100 animate-pulse"/>
    return (
        <img
            src={src}
            alt="图片消息"
            className="max-w-[260px] max-h-[280px] w-auto h-auto rounded border border-hair bg-white cursor-pointer object-cover"
            onClick={() => onPreview(src)}
        />
    )
}

function VideoMessageVideo({messageId, accountWxid, localSrc, onPreview}: {
    messageId: number
    accountWxid: string
    localSrc: string
    onPreview: (src: string) => void
}) {
    const [src, setSrc] = useState(localSrc)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (messageId <= 0 || !accountWxid) {
            setSrc(localSrc)
            return
        }
        let cancelled = false
        let objectURL = ''
        void apiBlob(`/messages/video?id=${messageId}&wxid=${encodeURIComponent(accountWxid)}`, {silent: true})
            .then((blob) => {
                if (cancelled) return
                objectURL = URL.createObjectURL(blob)
                setSrc(objectURL)
                setFailed(false)
            })
            .catch(() => {
                if (!cancelled && !localSrc) setFailed(true)
            })
        return () => {
            cancelled = true
            if (objectURL) URL.revokeObjectURL(objectURL)
        }
    }, [accountWxid, localSrc, messageId])

    if (failed) {
        return <div className="w-[200px] h-[140px] bg-black text-white/70 text-xs flex items-center justify-center">视频加载失败</div>
    }
    if (!src) return <div className="w-[200px] h-[140px] bg-black"/>
    return (
        <video
            src={src}
            preload="metadata"
            muted
            playsInline
            className="block max-w-[240px] max-h-[320px] w-auto h-auto object-cover"
            onLoadedMetadata={() => undefined}
            onClick={() => onPreview(src)}
        />
    )
}

function redPacketRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? value as Record<string, unknown> : {}
}

function redPacketText(detail: unknown, ...keys: string[]): string {
    const record = redPacketRecord(detail)
    for (const key of keys) {
        const value = record[key]
        if (typeof value === 'string' && value) return value
        if (typeof value === 'number') return String(value)
    }
    return ''
}

function redPacketMoney(detail: unknown, ...keys: string[]): string {
    const value = Number(redPacketText(detail, ...keys))
    return Number.isFinite(value) ? `${(value / 100).toFixed(2)} 元` : '--'
}

function redPacketRecords(detail: unknown): Record<string, unknown>[] {
    const record = redPacketRecord(detail)
    const rows = record.record || record.Record
    return Array.isArray(rows) ? rows.map(redPacketRecord) : []
}

function redPacketReceiverWxid(row: Record<string, unknown>): string {
    return redPacketText(row, 'userName', 'UserName', 'receiveOpenId', 'ReceiveOpenID')
}

function redPacketAlreadyOpened(detail: unknown): boolean {
    return Boolean(redPacketText(detail, 'receiveId', 'ReceiveID')) ||
        Number(redPacketText(detail, 'receiveStatus', 'ReceiveStatus')) > 0
}

function messageKind(message: ChatMessage): RenderedMessageKind {
    // Optimistic rows carry a definite msgType hint. Prefer that over the
    // content-sniffing fallback so the bubble renders the right shape from
    // the moment the user hits send.
    if (message.msgType === 'voice') return 'voice'
    if (message.msgType === 'video') return 'video'
    if (message.msgType === 'image') return 'image'
    if (message.msgType === 'file') return 'file'
    if (message.content.startsWith('[红包]') || message.content.startsWith('[红包封面]')) return 'red-packet'
    if (String(message.msgType) === '47') return 'emoji'
    if (String(message.msgType) === '62') return 'video'
    if (message.content.includes('[语音]')) return 'voice'
    if (message.content.includes('[视频]')) return 'video'
    if (message.content.includes('[小视频]')) return 'video'
    if (String(message.msgType) === '3' && message.content.includes('[图片]')) return 'image'
    if (message.content.includes('[表情]')) return 'emoji'
    if (message.content.includes('[文件]')) return 'file'
    return 'text'
}

function voipCallLabel(message: ChatMessage): string {
    if (String(message.msgType) !== '50') return ''
    const content = message.content.toLowerCase()
    if (/视频|video/.test(content)) return '[视频通话]'
    if (/语音|voice|audio|voip/.test(content)) return '[语音通话]'
    return '[语音通话]'
}

function messageTitle(message: ChatMessage, fallback: string) {
    return message.content.replace(/^\[[^\]]+]\s*/, '').trim() || fallback
}

function isRecalledMessage(message: ChatMessage): boolean {
    return message.revokedAt !== null && message.revokedAt !== undefined
}

function isGroupSystemNoticeContent(content: string): boolean {
    return /加入(?:了)?群聊|邀请.+加入(?:了)?群聊|通过扫描.+加入(?:了)?群聊|退出(?:了)?群聊|离开(?:了)?群聊|移出(?:了)?群聊|被.+移出(?:了)?群聊|修改群名|修改了群名称|设置了群公告|发布了群公告|解散(?:了)?群聊/.test(content)
}

function isPatNoticeMessage(message: ChatMessage): boolean {
    return String(message.msgType) === '10002' && /拍了拍/.test(message.content)
}

function isSystemNoticeMessage(message: ChatMessage): boolean {
    const msgType = String(message.msgType)
    return (
        msgType === '10000' ||
        msgType === '9999' ||
        isPatNoticeMessage(message) ||
        message.content.startsWith('[系统通知]') ||
        message.content === '[系统通知]' ||
        isGroupSystemNoticeContent(message.content)
    )
}

/** Hide system notices that have no meaningful content (e.g. repeated video call notifications) */
function isEmptySystemNotice(message: ChatMessage): boolean {
    return isSystemNoticeMessage(message) && (
        message.content === '[系统通知]' ||
        message.content.trim() === ''
    )
}

function isRejectedNoticeMessage(message: ChatMessage): boolean {
    return isSystemNoticeMessage(message) && message.content.includes('消息已发出，但被对方拒收了')
}

function sameMessageList(a: ChatMessage[], b: ChatMessage[]): boolean {
    if (a.length !== b.length) return false
    return a.every((item, index) => {
        const other = b[index]
        return Boolean(
            other &&
            item.id === other.id &&
            item.content === other.content &&
            item.msgType === other.msgType &&
            item.revokedAt === other.revokedAt &&
            item.localFilePath === other.localFilePath &&
            item.localPreviewUrl === other.localPreviewUrl,
        )
    })
}

function mergeServerMessages(current: ChatMessage[], serverMessages: ChatMessage[]): ChatMessage[] {
    const serverWithLocalVoicePreviews = serverMessages.map((server) => {
        const pending = current.find(
            (item) =>
                item.status === 'pending' &&
                item.direction === server.direction &&
                item.content === server.content &&
                item.msgType === 'voice' &&
                Boolean(item.localPreviewUrl),
        )
        return pending ? {...server, localPreviewUrl: pending.localPreviewUrl} : server
    })
    const pending = current.filter((item) => {
        if (item.status !== 'pending') return false
        return !serverWithLocalVoicePreviews.some(
            (server) => server.direction === item.direction && server.content === item.content,
        )
    })
    const next = pending.length ? [...serverWithLocalVoicePreviews, ...pending] : serverWithLocalVoicePreviews
    return sameMessageList(current, next) ? current : next
}

/** Format seconds as mm:ss (or hh:mm:ss when >= 1h). */
function formatDuration(totalSeconds: number): string {
    const total = Math.max(0, Math.floor(totalSeconds))
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60
    const pad = (n: number) => String(n).padStart(2, '0')
    if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`
    return `${pad(minutes)}:${pad(seconds)}`
}

type ContextMenuState = {
    visible: boolean
    x: number
    y: number
    message: ChatMessage | null
}

type SendResponse = {
    success?: boolean
    results?: Array<{success?: boolean; message?: string}>
}

type GroupMemberMessageStat = {
    messageCount: number
    lastActiveAt?: number
}

const MIN_COMPOSER_HEIGHT = 120
const MAX_COMPOSER_HEIGHT = 520
const DEFAULT_COMPOSER_HEIGHT = 280
const COMPOSER_HEIGHT_STORAGE_KEY = 'wechat-web-composer-height'

export function RealtimeConversationView({account: currentAccount, accounts = [], contacts, resolveContact, updateContact, settings}: {
    account: Account | null
    accounts?: AccountItem[]
    contacts: Contact[]
    resolveContact: (wxid: string) => Promise<Contact | null>
    updateContact: (wxid: string, update: Partial<Contact> | ((contact: Contact) => Contact)) => void
    settings: import('@/types').SettingsState
}) {
    const searchParams = useSearchParams()
    const router = useRouter()
    const activeWxid = searchParams.get('conversationWxid') || searchParams.get('wxid') || ''
    const activeAccountWxid = searchParams.get('accountWxid') || currentAccount?.wxid || ''
    const account = useMemo<Account | null>(() => {
        if (!activeAccountWxid) return currentAccount
        if (currentAccount?.wxid === activeAccountWxid) return currentAccount
        const found = accounts.find((item) => item.wxid === activeAccountWxid)
        if (!found) {
            return {
                wxid: activeAccountWxid,
                nickName: activeAccountWxid,
            }
        }
        return {
            wxid: found.wxid,
            nickName: found.nickName || found.wxid,
            avatar: found.avatar,
        }
    }, [accounts, activeAccountWxid, currentAccount])

    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [openingRedPacketIds, setOpeningRedPacketIds] = useState<Set<number>>(new Set())
    const [openedRedPacketIds, setOpenedRedPacketIds] = useState<Set<number>>(new Set())
    const [redPacketDetail, setRedPacketDetail] = useState<unknown | null>(null)
    const [convertedTexts, setConvertedTexts] = useState<Record<number, string>>({})
    const [transcribingIds, setTranscribingIds] = useState<Set<number>>(new Set())
    const [composerPayload, setComposerPayload] = useState<ReplyPayload>(() =>
        emptyReplyPayload('text'),
    )
    const [sending, setSending] = useState(false)
    const [autoGrabRedPacket, setAutoGrabRedPacket] = useState(false)
    const [autoGrabRedPacketLoading, setAutoGrabRedPacketLoading] = useState(false)
    const [showSummaryModal, setShowSummaryModal] = useState(false)
    const [summaryContent, setSummaryContent] = useState('')
    const [summaryLoading, setSummaryLoading] = useState(false)
    const [summaryRange, setSummaryRange] = useState<'today' | 'yesterday' | '3days'>('today')
    const [aiReplyLoading, setAiReplyLoading] = useState(false)
    const [showContactInfoSidebar, setShowContactInfoSidebar] = useState(() => {
        try { return localStorage.getItem('contactInfoSidebarOpen') === 'true' } catch { return false }
    })
    const [sidebarView, setSidebarView] = useState<'profile' | 'moments' | 'members'>('profile')
    const [contactInfoPreviewOpen, setContactInfoPreviewOpen] = useState(false)
    const [sidebarLabels, setSidebarLabels] = useState<ContactLabel[]>([])
    const [sidebarMoments, setSidebarMoments] = useState<ConversationMoment[]>([])
    const [sidebarMomentsLoading, setSidebarMomentsLoading] = useState(false)
    const [sidebarMomentsSyncing, setSidebarMomentsSyncing] = useState(false)
    const [sidebarMomentsLoadedFor, setSidebarMomentsLoadedFor] = useState('')
    const [sidebarPulling, setSidebarPulling] = useState(false)
    const [sidebarActingMomentId, setSidebarActingMomentId] = useState('')
    const [sidebarCommentingMomentId, setSidebarCommentingMomentId] = useState('')
    const [sidebarCommentText, setSidebarCommentText] = useState('')
    const [momentPreviewImage, setMomentPreviewImage] = useState('')
    const [editingRemark, setEditingRemark] = useState(false)
    const [remarkValue, setRemarkValue] = useState('')
    const [chatNoteValue, setChatNoteValue] = useState('')
    const [contactMuted, setContactMuted] = useState(false)
    const [isPinned, setIsPinned] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [memberSearchTerm, setMemberSearchTerm] = useState('')
    const [groupMembers, setGroupMembers] = useState<Contact[]>([])
    const [groupMemberStats, setGroupMemberStats] = useState<Record<string, GroupMemberMessageStat>>({})
    const [groupMembersLoading, setGroupMembersLoading] = useState(false)
    const [memberAvatarPreview, setMemberAvatarPreview] = useState('')
    const [addingFriendWxid, setAddingFriendWxid] = useState('')
    const [groupMemberFriendStatus, setGroupMemberFriendStatus] = useState<Record<string, 'checking' | 'friend' | 'notFriend'>>({})
    const [groupMembersLoadedFor, setGroupMembersLoadedFor] = useState('')
    const [previewState, setPreviewState] = useState<{thumb: string; hd: string; id?: number} | null>(null)
    const [videoPreview, setVideoPreview] = useState<{src: string; id: number} | null>(null)
    const [composerHeight, setComposerHeight] = useState(DEFAULT_COMPOSER_HEIGHT)
    const [isNarrowViewport, setIsNarrowViewport] = useState(false)
    const [resizingComposer, setResizingComposer] = useState(false)
    const composerHeightRef = useRef(DEFAULT_COMPOSER_HEIGHT)
    const effectiveComposerHeight = isNarrowViewport ? Math.min(composerHeight, 190) : composerHeight
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({
        visible: false,
        x: 0,
        y: 0,
        message: null,
    })
    const [avatarPopover, setAvatarPopover] = useState<{rect: DOMRect; message: ChatMessage} | null>(null)

    const historyRef = useRef<HTMLDivElement | null>(null)
    const momentsScrollRef = useRef<HTMLDivElement | null>(null)
    const momentsTouchStartYRef = useRef<number | null>(null)
    const bottomAnchorRef = useRef<HTMLDivElement | null>(null)
    const groupMembersRequestRef = useRef('')
    const contactResolveAttemptedRef = useRef<Set<string>>(new Set())
    const skipNextAutoScrollRef = useRef(false)
    const skipRemarkSaveRef = useRef(false)
    const [loadingOlder, setLoadingOlder] = useState(false)
    const [hasMoreOlder, setHasMoreOlder] = useState(true)

    function forceScrollToBottom(behavior: ScrollBehavior = 'auto') {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                bottomAnchorRef.current?.scrollIntoView({block: 'end', behavior})
            })
        })
    }

    const contactMap = useMemo(
        () => new Map(contacts.map((contact) => [contact.wxid, contact])),
        [contacts],
    )
    const groupMemberMap = useMemo(
        () => new Map(groupMembers.map((member) => [member.wxid, member])),
        [groupMembers],
    )
    const filteredGroupMembers = useMemo(() => {
        const q = memberSearchTerm.trim().toLowerCase()
        return groupMembers.filter((member) => {
            if (!q) return true
            const name = (member.remark || member.nickName || member.name || member.wxid).toLowerCase()
            return name.includes(q) || member.wxid.toLowerCase().includes(q)
        })
    }, [groupMembers, memberSearchTerm])

    const selectedContact = activeWxid ? contactMap.get(activeWxid) : undefined
    const selectedName = selectedContact ? displayName(selectedContact) : activeWxid
    const isGroup = activeWxid.endsWith('@chatroom')
    const mentionOptions = isGroup ? (() => {
        const seen = new Set<string>()
        return [
            {wxid: 'notify@all', label: '所有人'},
            ...groupMembers.flatMap((member) => {
                if (!member.wxid || seen.has(member.wxid)) return []
                seen.add(member.wxid)
                return [{
                    wxid: member.wxid,
                    label: displayName(member),
                    avatar: member.avatar || contactLatestAvatarUrl(member),
                }]
            }),
        ]
    })() : []
    const commitRemark = useCallback(() => {
        if (skipRemarkSaveRef.current) {
            skipRemarkSaveRef.current = false
            return
        }
        if (!account || !selectedContact) {
            setEditingRemark(false)
            return
        }
        const nextRemark = remarkValue.trim()
        setRemarkValue(nextRemark)
        setEditingRemark(false)
        if (nextRemark === (selectedContact.remark || '')) return
        void apiPost('/contacts/remark', {
            wxid: account.wxid,
            targetWxid: selectedContact.wxid,
            remark: nextRemark,
        }, {silent: true}).catch(() => {})
    }, [account, remarkValue, selectedContact])
    const canShowMomentsTab = Boolean(
        selectedContact &&
        !isGroup &&
        activeWxid !== account?.wxid &&
        !activeWxid.startsWith('gh_') &&
        !activeWxid.startsWith('weixin') &&
        !activeWxid.startsWith('filehelper'),
    )

    const handleAiSummary = async (range: 'today' | 'yesterday' | '3days' = summaryRange) => {
        if (!account || !activeWxid || summaryLoading) return
        setShowSummaryModal(true)
        setSummaryLoading(true)
        setSummaryContent('')
        setSummaryRange(range)
        try {
            const result = await apiPost<{success: boolean; summary: string; messageCount: number; error?: string}>(
                '/messages/summary',
                {
                    wxid: account.wxid,
                    conversationWxid: activeWxid,
                    range,
                },
            )
            if (result.success) {
                setSummaryContent(result.summary)
            } else {
                setSummaryContent(result.error || '生成摘要失败')
            }
        } catch (err) {
            setSummaryContent(`生成摘要失败：${err instanceof Error ? err.message : '未知错误'}`)
        } finally {
            setSummaryLoading(false)
        }
    }

    const loadGroupMembers = useCallback(async ({showLoading = false, force = false}: {
        showLoading?: boolean
        force?: boolean
    } = {}) => {
        if (!account || !activeWxid || !isGroup) return
        const roomWxid = activeWxid
        if (!force && groupMembersLoadedFor === roomWxid) return
        if (groupMembersRequestRef.current === roomWxid) return
        groupMembersRequestRef.current = roomWxid
        if (showLoading) setGroupMembersLoading(true)
        try {
            const response = await apiPost<{success: boolean; members: Contact[]; stats?: Record<string, GroupMemberMessageStat>}>('/group-members', {
                wxid: account.wxid,
                ChatRoomName: roomWxid,
            }, {silent: !showLoading})
            if (groupMembersRequestRef.current === roomWxid) {
                setGroupMembers(response.members || [])
                setGroupMemberStats(response.stats || {})
                setGroupMembersLoadedFor(roomWxid)
            }
        } catch {
            if (groupMembersRequestRef.current === roomWxid) {
                if (showLoading) setGroupMembers([])
                if (showLoading) setGroupMemberStats({})
                setGroupMembersLoadedFor(roomWxid)
            }
        } finally {
            if (groupMembersRequestRef.current === roomWxid) {
                groupMembersRequestRef.current = ''
                if (showLoading) setGroupMembersLoading(false)
            }
        }
    }, [account, activeWxid, groupMembersLoadedFor, isGroup])

    const handleShowGroupMembers = async () => {
        if (!account || !activeWxid) return
        setShowContactInfoSidebar(true)
        setSidebarView('members')
        setMemberSearchTerm('')
        try { localStorage.setItem('contactInfoSidebarOpen', 'true') } catch { /* ignore */ }
        await loadGroupMembers({showLoading: true, force: groupMembers.length === 0})
    }

    const handleAddGroupMemberFriend = async (member: Contact) => {
        if (!account || !activeWxid || addingFriendWxid) return
        setAddingFriendWxid(member.wxid)
        try {
            const response = await apiPost<{success: boolean; message?: string}>('/friends/add', {
                wxid: account.wxid,
                targetWxid: member.wxid,
                ChatRoomUserName: activeWxid,
                verifyContent: '你好，我是群里的朋友',
            })
            if (response.success === false) {
                message.error(response.message || '添加好友失败')
                return
            }
            setGroupMemberFriendStatus((current) => ({...current, [member.wxid]: 'friend'}))
            message.success('好友申请已发送')
        } catch (error) {
            message.error(error instanceof Error ? error.message : '添加好友失败')
        } finally {
            setAddingFriendWxid('')
        }
    }

    function openDirectMessage(wxid: string) {
        if (!wxid) return
        setAvatarPopover(null)
        const params = new URLSearchParams({conversationWxid: wxid})
        if (account?.wxid) params.set('accountWxid', account.wxid)
        router.push(`/conversation/realtime?${params.toString()}`)
    }

    function groupMemberStatus(member: Contact) {
        if (member.wxid === account?.wxid) return 'friend'
        if (contactMap.has(member.wxid)) return 'friend'
        return groupMemberFriendStatus[member.wxid]
    }

    function groupMemberActionLabel(member: Contact) {
        if (member.wxid === account?.wxid) return '自己'
        const status = groupMemberStatus(member)
        if (status === 'friend') return '发消息'
        if (status === 'checking') return '检测中...'
        return '添加好友'
    }

    async function handleGroupMemberAction(member: Contact) {
        const status = groupMemberStatus(member)
        if (status === 'friend') {
            openDirectMessage(member.wxid)
            return
        }
        if (status === 'checking') return
        if (status === 'notFriend') {
            await handleAddGroupMemberFriend(member)
            return
        }
        await checkGroupMemberFriendStatus(member.wxid).then((nextStatus) => {
            if (nextStatus === 'friend') {
                openDirectMessage(member.wxid)
                return
            }
            void handleAddGroupMemberFriend(member)
        })
    }

    function handleContactLabelToggle(labelId: number, selected: boolean) {
        if (!account || !selectedContact) return
        const previousLabelIds = selectedContact.labelIds || ''
        const current = parseContactLabelIds(previousLabelIds)
        const next = selected ? current.filter((id) => id !== labelId) : [...current, labelId]
        const nextLabelIds = next.join(',')
        const targetWxid = selectedContact.wxid

        updateContact(targetWxid, {labelIds: nextLabelIds})
        apiPost('/contacts/labels', {
            wxid: account.wxid,
            targetWxid,
            labelIds: nextLabelIds,
        }, {silent: true}).catch(() => {
            updateContact(targetWxid, (contact) => (
                contact.labelIds === nextLabelIds ? {...contact, labelIds: previousLabelIds} : contact
            ))
            message.error('标签更新失败')
        })
    }

    function ensureGroupMemberFriendStatus(wxid: string) {
        void checkGroupMemberFriendStatus(wxid)
    }

    function checkGroupMemberFriendStatus(wxid: string): Promise<'friend' | 'notFriend' | 'checking' | undefined> {
        if (!account || !wxid) return Promise.resolve(undefined)
        if (wxid === account.wxid) {
            setGroupMemberFriendStatus((current) => ({...current, [wxid]: 'friend'}))
            return Promise.resolve('friend')
        }
        if (contactMap.has(wxid)) {
            setGroupMemberFriendStatus((current) => ({...current, [wxid]: 'friend'}))
            return Promise.resolve('friend')
        }
        if (groupMemberFriendStatus[wxid]) return Promise.resolve(groupMemberFriendStatus[wxid])
        setGroupMemberFriendStatus((current) => ({...current, [wxid]: 'checking'}))
        return apiPost<{success: boolean; level?: number; response?: unknown}>(
            '/friend-status',
            {
                wxid: account.wxid,
                userName: wxid,
            },
            {silent: true},
        )
            .then((response) => {
                const relation = getAnyNumber(response.response, ['FriendRelation', 'friendRelation'])
                const isFriend = response.level === 0 || relation === 0
                setGroupMemberFriendStatus((current) => ({
                    ...current,
                    [wxid]: isFriend ? 'friend' : 'notFriend',
                }))
                return isFriend ? 'friend' : 'notFriend'
            })
            .catch(() => {
                setGroupMemberFriendStatus((current) => ({...current, [wxid]: 'notFriend'}))
                return 'notFriend'
            })
    }

    // If the active conversation partner is missing from the local
    // contacts cache, resolve it on-demand so the chat header and the
    // avatar popover both have a real name + avatar to render. No-ops
    // when the contact is already cached. Groups with an empty avatar are
    // refreshed once so the chat list/header can replace the placeholder.
    useEffect(() => {
        if (!account || !activeWxid) return
        if (activeWxid.startsWith('gh_')) return
        const cached = contactMap.get(activeWxid)
        if (cached && (!activeWxid.endsWith('@chatroom') || cached.avatar)) return
        if (activeWxid.endsWith('@chatroom')) {
            const attemptKey = `${account.wxid}:${activeWxid}`
            if (contactResolveAttemptedRef.current.has(attemptKey)) return
            contactResolveAttemptedRef.current.add(attemptKey)
        }
        void resolveContact(activeWxid)
    }, [account, activeWxid, contactMap, resolveContact])

    useEffect(() => {
        if (!account || !activeWxid) {
            setAutoGrabRedPacket(false)
            return
        }
        let cancelled = false
        void apiGet<{enabled: boolean}>(
            '/messages/red-packet/auto-grab?wxid=' +
            encodeURIComponent(account.wxid) +
            '&conversationWxid=' +
            encodeURIComponent(activeWxid),
        ).then((response) => {
            if (!cancelled) setAutoGrabRedPacket(response.enabled === true)
        }).catch(() => {
            if (!cancelled) setAutoGrabRedPacket(false)
        })
        return () => {
            cancelled = true
        }
    }, [account, activeWxid])

    useEffect(() => {
        const media = window.matchMedia('(max-width: 1024px)')
        const update = () => setIsNarrowViewport(media.matches)
        update()
        media.addEventListener('change', update)
        return () => media.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        const raw = window.localStorage.getItem(COMPOSER_HEIGHT_STORAGE_KEY)
        if (!raw) return
        const value = Number(raw)
        if (Number.isFinite(value) && value >= MIN_COMPOSER_HEIGHT && value <= MAX_COMPOSER_HEIGHT) {
            composerHeightRef.current = value
            setComposerHeight(value)
        }
    }, [])

    useEffect(() => {
        if (!resizingComposer) return
        const onMove = (event: globalThis.MouseEvent) => {
            const viewport = window.innerHeight
            const next = Math.min(Math.max(viewport - event.clientY, MIN_COMPOSER_HEIGHT), MAX_COMPOSER_HEIGHT)
            composerHeightRef.current = next
            setComposerHeight(next)
        }
        const onUp = () => {
            setResizingComposer(false)
            window.localStorage.setItem(COMPOSER_HEIGHT_STORAGE_KEY, String(composerHeightRef.current))
        }
        document.body.style.cursor = 'row-resize'
        document.body.style.userSelect = 'none'
        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onUp)
        return () => {
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            document.removeEventListener('mousemove', onMove)
            document.removeEventListener('mouseup', onUp)
        }
    }, [resizingComposer])

    const refreshCurrentMessages = useCallback(async () => {
        if (!account || !activeWxid) return
        const response = await apiGet<{success: boolean; messages: ChatMessage[]}>(
            '/messages?wxid=' +
                encodeURIComponent(account.wxid) +
                '&conversationWxid=' +
                encodeURIComponent(activeWxid),
            {silent: true},
        )
        setMessages((current) => mergeServerMessages(current, response.messages || []))
    }, [account, activeWxid])

    const loadSidebarMoments = useCallback(async () => {
        if (!account || !activeWxid) return
        setSidebarMomentsLoading(true)
        try {
            const response = await apiPost<{success: boolean; response: unknown}>(
                '/moments/user-page',
                {
                    wxid: account.wxid,
                    FirstPageMD5: '',
                    MaxID: 0,
                    UserName: activeWxid,
                },
                {silent: true},
            )
            setSidebarMoments(extractProtocolMoments(response.response))
            setSidebarMomentsLoadedFor(activeWxid)
        } catch {
            setSidebarMoments([])
            setSidebarMomentsLoadedFor('')
        } finally {
            setSidebarMomentsLoading(false)
        }
    }, [account, activeWxid])

    const syncSidebarMoments = useCallback(async () => {
        if (!account || sidebarMomentsSyncing) return
        setSidebarMomentsSyncing(true)
        setSidebarPulling(false)
        try {
            await loadSidebarMoments()
        } catch (error) {
            message.error(error instanceof Error ? error.message : '朋友圈刷新失败')
        } finally {
            setSidebarMomentsSyncing(false)
        }
    }, [account, sidebarMomentsSyncing, loadSidebarMoments])

    const triggerSidebarPullRefresh = useCallback(() => {
        if (sidebarMomentsLoading || sidebarMomentsSyncing) return
        void syncSidebarMoments()
    }, [sidebarMomentsLoading, sidebarMomentsSyncing, syncSidebarMoments])

    const runSidebarMomentAction = useCallback(async (moment: ConversationMoment, action: 'like' | 'comment', content = '') => {
        if (!account || sidebarActingMomentId) return
        setSidebarActingMomentId(moment.momentId)
        try {
            const response = await apiPost<{success: boolean; message?: string}>('/moments/action', {
                wxid: account.wxid,
                momentId: moment.momentId,
                targetWxid: moment.authorWxid,
                action,
                content,
            })
            if (response.success === false) {
                message.error(response.message || '朋友圈操作失败')
                return
            }
            setSidebarMoments((items) => items.map((item) => {
                if (item.momentId !== moment.momentId) return item
                if (action === 'like') {
                    const selfName = account.nickName || account.alias || account.wxid
                    const hasSelf = item.likedBy.some((like) => like.wxid === account.wxid || like.author === selfName)
                    return {
                        ...item,
                        liked: true,
                        likeCount: item.liked ? item.likeCount : item.likeCount + 1,
                        likedBy: hasSelf ? item.likedBy : [...item.likedBy, {author: selfName, wxid: account.wxid}],
                    }
                }
                return {
                    ...item,
                    comments: [
                        ...item.comments,
                        {
                            author: account.nickName || account.alias || account.wxid,
                            content,
                        },
                    ],
                }
            }))
            if (action === 'comment') {
                setSidebarCommentingMomentId('')
                setSidebarCommentText('')
            }
        } catch (error) {
            message.error(error instanceof Error ? error.message : '朋友圈操作失败')
        } finally {
            setSidebarActingMomentId('')
        }
    }, [account, sidebarActingMomentId])

    useEffect(() => {
        if (!account || !activeWxid) {
            setMessages([])
            return
        }
        let cancelled = false
        void refreshCurrentMessages()
            .then(() => {
                if (!cancelled) forceScrollToBottom()
            })
            .catch(() => {
                if (!cancelled) setMessages([])
            })
        // const timer = window.setInterval(() => {
        //     void refreshCurrentMessages()
        // }, 3000)
        return () => {
            cancelled = true
            // window.clearInterval(timer)
        }
    }, [account, activeWxid, refreshCurrentMessages])

    useEffect(() => {
        if (!showContactInfoSidebar || sidebarView !== 'moments' || !account || !canShowMomentsTab) return
        if (sidebarMomentsLoadedFor === activeWxid) return
        void loadSidebarMoments()
    }, [showContactInfoSidebar, sidebarView, account, activeWxid, sidebarMomentsLoadedFor, loadSidebarMoments, canShowMomentsTab])

    // Load older messages when scrolling to top
    const loadOlderMessages = useCallback(async () => {
        if (!account || !activeWxid || loadingOlder || !hasMoreOlder) return
        const oldestMessage = messages[0]
        if (!oldestMessage) return
        const scrollNode = historyRef.current
        const previousScrollHeight = scrollNode?.scrollHeight ?? 0
        const previousScrollTop = scrollNode?.scrollTop ?? 0
        setLoadingOlder(true)
        try {
            const response = await apiGet<{success: boolean; messages: ChatMessage[]}>(
                '/messages?wxid=' +
                    encodeURIComponent(account.wxid) +
                    '&conversationWxid=' +
                    encodeURIComponent(activeWxid) +
                    '&beforeId=' + oldestMessage.id,
            )
            const older = response.messages || []
            if (older.length === 0) {
                setHasMoreOlder(false)
            } else {
                skipNextAutoScrollRef.current = true
                let insertedOlder = false
                setMessages((current) => {
                    const existingIds = new Set(current.map(m => m.id))
                    const newOnes = older.filter(m => !existingIds.has(m.id))
                    if (newOnes.length === 0) {
                        return current
                    }
                    insertedOlder = true
                    return [...newOnes, ...current]
                })
                if (!insertedOlder || older.length < 200) {
                    setHasMoreOlder(false)
                }
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        const node = historyRef.current
                        if (!node) return
                        const nextScrollHeight = node.scrollHeight
                        node.scrollTop = nextScrollHeight - previousScrollHeight + previousScrollTop
                    })
                })
            }
        } catch {
            // silently fail
        } finally {
            setLoadingOlder(false)
        }
    }, [account, activeWxid, loadingOlder, hasMoreOlder, messages])

    // Reset hasMoreOlder and group members when conversation changes
    useEffect(() => {
        skipNextAutoScrollRef.current = false
        setMessages([])
        setHasMoreOlder(true)
        setGroupMembers([])
        setGroupMemberStats({})
        setGroupMembersLoading(false)
        setGroupMembersLoadedFor('')
        groupMembersRequestRef.current = ''
        setGroupMemberFriendStatus({})
        setSidebarMoments([])
        setSidebarMomentsLoadedFor('')
        setSidebarView('profile')
    }, [account?.wxid, activeWxid])

    useEffect(() => {
        if (!isGroup || !account || !activeWxid) return
        void loadGroupMembers()
    }, [isGroup, account, activeWxid, loadGroupMembers])

    useEffect(() => {
        if (!canShowMomentsTab && sidebarView === 'moments') {
            setSidebarView('profile')
        }
        if (!isGroup && sidebarView === 'members') {
            setSidebarView('profile')
        }
    }, [canShowMomentsTab, isGroup, sidebarView])

    // Load labels and conversation-specific data when sidebar opens or conversation changes
    useEffect(() => {
        if (!showContactInfoSidebar || !account || !activeWxid) return
        if (selectedContact) {
            apiGet<{success: boolean; labels: ContactLabel[]}>(`/labels?wxid=${encodeURIComponent(account.wxid)}`, {silent: true})
                .then((res) => setSidebarLabels(res.labels || []))
                .catch(() => setSidebarLabels([]))
            setRemarkValue(selectedContact.remark || '')
            setContactMuted(Boolean(selectedContact.muted))
        } else {
            setSidebarLabels([])
            setRemarkValue('')
            setContactMuted(false)
        }
        apiGet<{success: boolean; conversations: Array<{conversationWxid: string; pinned: boolean; chatNote?: string}>}>(`/conversations?wxid=${encodeURIComponent(account.wxid)}`, {silent: true})
            .then((res) => {
                const conv = res.conversations?.find((c) => c.conversationWxid === activeWxid)
                setIsPinned(Boolean(conv?.pinned))
                setChatNoteValue(conv?.chatNote || selectedContact?.chatNote || '')
            })
            .catch(() => {
                setIsPinned(false)
                setChatNoteValue(selectedContact?.chatNote || '')
            })
    }, [showContactInfoSidebar, activeWxid, account, selectedContact])

    // Scroll-to-top detection
    useEffect(() => {
        const el = historyRef.current
        if (!el) return
        const handleScroll = () => {
            if (el.scrollTop < 80 && hasMoreOlder && !loadingOlder) {
                void loadOlderMessages()
            }
        }
        el.addEventListener('scroll', handleScroll, {passive: true})
        return () => el.removeEventListener('scroll', handleScroll)
    }, [hasMoreOlder, loadingOlder, loadOlderMessages])

    useEffect(() => {
        if (!account) return
        const onMessage = (event: Event) => {
            const message = (event as CustomEvent<ChatMessage>).detail
            if (!message) return
            void refreshCurrentMessages().catch(() => undefined)
            if (message.conversationWxid !== activeWxid) return
            // If the sender (or conversation partner for a group) is not
            // in the local contacts cache, resolve it on-demand so the
            // header + avatar popover + sidebar can show a real name
            // rather than the raw wxid.
            const senderWxid = message.direction === 'in'
                ? (message.fromWxid || message.conversationWxid)
                : ''
            if (senderWxid && !activeWxid.endsWith('@chatroom')) {
                void resolveContact(senderWxid)
            }
            setMessages((current) => {
                // If we already have this id, merge the server copy in-place.
                // This covers revoke updates, where SSE sends the original
                // message row again with `revokedAt` set.
                if (current.some((item) => item.id === message.id)) {
                    return current.map((item) => (item.id === message.id ? {...item, ...message} : item))
                }
                // Reconcile with an optimistic placeholder if any. Match on
                // direction + content + rough timestamp so the bubble doesn't
                // visually jump from temp to real.
                const pendingIdx = current.findIndex(
                    (item) =>
                        item.status === 'pending' &&
                        item.direction === message.direction &&
                        item.content === message.content,
                )
                if (pendingIdx >= 0) {
                    const pending = current[pendingIdx]!
                    const next = current.slice()
                    const keepLocalPreview = pending.msgType === 'voice' && Boolean(pending.localPreviewUrl)
                    if (pending.localPreviewUrl && !keepLocalPreview) URL.revokeObjectURL(pending.localPreviewUrl)
                    next[pendingIdx] = {
                        ...message,
                        status: 'sent',
                        localPreviewUrl: keepLocalPreview ? pending.localPreviewUrl : message.localPreviewUrl,
                    }
                    return next
                }
                return [...current, message]
            })
        }
        window.addEventListener(APP_MESSAGE_EVENT, onMessage)
        return () => {
            window.removeEventListener(APP_MESSAGE_EVENT, onMessage)
        }
    }, [account, activeWxid, resolveContact, refreshCurrentMessages])

    // Scroll-to-bottom. Uses a dedicated anchor <div> at the very end of
    // the message list — far more reliable than `scrollHeight` math, which
    // can be stale before images/videos lay themselves out. rAF defers to
    // the next paint so React's DOM commit is finished.
    //
    // Secondary observer: the ResizeObserver below catches late layout
    // growth (image `onLoad`, video `preload="metadata"`) and re-anchors
    // only when the user is already near the bottom, so scrolling up to
    // read history isn't hijacked.
    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
        bottomAnchorRef.current?.scrollIntoView({block: 'end', behavior})
    }, [])

    useEffect(() => {
        if (skipNextAutoScrollRef.current) {
            skipNextAutoScrollRef.current = false
            return
        }
        requestAnimationFrame(() => scrollToBottom())
    }, [messages, composerHeight, activeWxid, scrollToBottom])

    useEffect(() => {
        const node = historyRef.current
        if (!node || typeof ResizeObserver === 'undefined') return

        let wasAtBottom = true
        const NEAR_BOTTOM_PX = 120

        const checkAtBottom = () => {
            wasAtBottom = node.scrollHeight - node.scrollTop - node.clientHeight < NEAR_BOTTOM_PX
        }
        node.addEventListener('scroll', checkAtBottom, {passive: true})

        const observer = new ResizeObserver(() => {
            if (wasAtBottom) scrollToBottom()
        })
        for (const child of Array.from(node.children)) {
            observer.observe(child)
        }
        observer.observe(node)

        return () => {
            node.removeEventListener('scroll', checkAtBottom)
            observer.disconnect()
        }
    }, [activeWxid, scrollToBottom])

    useEffect(() => {
        if (!contextMenu.visible) return
        const close = () => setContextMenu({visible: false, x: 0, y: 0, message: null})
        document.addEventListener('click', close)
        return () => document.removeEventListener('click', close)
    }, [contextMenu.visible])

    useEffect(() => {
        // Reset composer when switching conversations
        setComposerPayload(emptyReplyPayload('text'))
        setAvatarPopover(null)
    }, [activeWxid])

    const hasContent =
        !!(composerPayload.content || '').trim() ||
        (composerPayload.parts || []).length > 0
    const canSend = !!activeWxid && !!account && hasContent && !sending

    async function handleGenerateAIReply() {
        if (!account || !activeWxid || aiReplyLoading) return
        setAiReplyLoading(true)
        try {
            const result = await apiPost<{
                success: boolean
                reply: string
                messageCount: number
                error?: string
                message?: string
            }>('/messages/ai-reply', {
                wxid: account.wxid,
                conversationWxid: activeWxid,
                historyLimit: settings.system?.aiReplyHistoryLimit ?? settings.aiConfig?.replyHistoryLimit ?? 50,
                prompt: settings.system?.aiReplyPrompt || '',
            })
            if (!result.success) {
                message.error(result.error || result.message || 'AI 生成回复失败')
                return
            }
            const reply = (result.reply || '').trim()
            if (!reply) {
                message.warning('AI 没有生成可用回复')
                return
            }
            setComposerPayload((current) => ({
                ...current,
                type: 'text',
                content: reply,
            }))
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'AI 生成回复失败')
        } finally {
            setAiReplyLoading(false)
        }
    }

    /**
     * Build an optimistic ChatMessage for instant bubble rendering. The row
     * gets a negative id so it never collides with DB-assigned positive ids,
     * and a `clientId` that the server echoes back to let us reconcile on
     * response / SSE arrival.
     */
    function makeOptimisticMessage(
        content: string,
        extras: {localPreviewUrl?: string; msgType?: string} = {},
    ): ChatMessage {
        if (!account) {
            // Defensive — caller must have account, type demands non-null.
            throw new Error('account required')
        }
        const clientId = `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        return {
            id: -Date.now() - Math.floor(Math.random() * 1000),
            accountWxid: account.wxid,
            conversationWxid: activeWxid,
            fromWxid: account.wxid,
            toWxid: activeWxid,
            content,
            msgType: extras.msgType || 'text',
            direction: 'out',
            createdAt: Date.now(),
            status: 'pending',
            clientId,
            localPreviewUrl: extras.localPreviewUrl,
        }
    }

    /**
     * Mark the placeholder 'failed' so the user sees which bubble couldn't
     * be delivered. The server never sends 'failed' over SSE; this is purely
     * a client-side UI signal.
     */
    function markOptimisticFailed(clientId: string, reason?: string) {
        setMessages((current) =>
            current.map((item) =>
                item.clientId === clientId
                    ? {
                        ...item,
                        status: 'failed',
                        content: reason ? `${item.content}（${reason}）` : item.content,
                    }
                    : item,
            ),
        )
    }

    function assertSendSucceeded(response: SendResponse) {
        if (response.success !== false) return
        const reason = response.results?.find((item) => item.success === false)?.message || '发送失败'
        throw new Error(reason)
    }

    async function prepareVoicePartForSend(part: ReplyPayload): Promise<ReplyPayload> {
        try {
            return await encodeReplyVoicePartForWechat(part)
        } catch {
            if (part.base64) return part
            throw new Error('语音编码失败')
        }
    }

    async function sendTextOnly(content: string, at: string[] = []) {
        if (!account || !content) return
        const optimistic = makeOptimisticMessage(content)
        setMessages((current) => [...current, optimistic])
        try {
            const response = await apiPost<SendResponse>(
                '/messages/send',
                {
                    wxid: account.wxid,
                    targets: [activeWxid],
                    payload: {
                        type: 'text',
                        content,
                        ...(at.length ? {at} : {}),
                    },
                    clientId: optimistic.clientId,
                },
                {silent: true},
            )
            assertSendSucceeded(response)
        } catch (err) {
            markOptimisticFailed(
                optimistic.clientId!,
                err instanceof Error ? err.message : '发送失败',
            )
            throw err
        }
    }

    async function sendPart(part: ReplyPayload) {
        if (!account) return
        const type = part.type || 'file'
        const displayContent =
            type === 'image'
                ? '[图片]'
                : type === 'voice'
                  ? `[语音] ${Math.max(1, Math.round(part.playLength || 1))}"`
                  : type === 'video'
                    ? '[视频]'
                    : type === 'file'
                      ? `[文件] ${part.fileName || '文件'}`
                      : part.content || ''
        const optimistic = makeOptimisticMessage(displayContent, {
            localPreviewUrl: part.previewBase64 || part.base64,
            msgType: type,
        })
        setMessages((current) => [...current, optimistic])
        try {
            const outboundPart = type === 'voice'
                ? await prepareVoicePartForSend(part)
                : part
            const {
                previewBase64: _previewBase64,
                rawBase64: _rawBase64,
                ...payloadToSend
            } = outboundPart
            const sendPayload = type === 'voice'
                ? outboundPart
                : payloadToSend
            const response = await apiPost<SendResponse>(
                '/messages/send',
                {
                    wxid: account.wxid,
                    targets: [activeWxid],
                    type,
                    payload: sendPayload,
                    content: outboundPart.content,
                    clientId: optimistic.clientId,
                },
                {silent: true},
            )
            assertSendSucceeded(response)
        } catch (err) {
            markOptimisticFailed(
                optimistic.clientId!,
                err instanceof Error ? err.message : '发送失败',
            )
            throw err
        }
    }

    async function handleSendAll() {
        if (!canSend || !account) return
        setSending(true)
        // Snapshot the composer and clear it immediately so the optimistic
        // bubble renders without a flicker.
        const snapshot = composerPayload
        setComposerPayload(emptyReplyPayload('text'))
        try {
            const textToSend = (snapshot.content || '').trim()
            if (textToSend) {
                try {
                    await sendTextOnly(textToSend, snapshot.at || [])
                } catch {
                    /* already reflected in the failed bubble */
                }
            }
            for (const part of snapshot.parts || []) {
                try {
                    await sendPart(part)
                } catch {
                    /* already reflected in the failed bubble */
                }
            }
        } finally {
            setSending(false)
        }
    }

    function openContextMenu(event: MouseEvent, message: ChatMessage) {
        event.preventDefault()
        setContextMenu({visible: true, x: event.clientX, y: event.clientY, message})
    }

    async function copyMessage(msg: ChatMessage) {
        try {
            await navigator.clipboard.writeText(msg.content)
            message.success('已复制')
        } catch {
            message.error('复制失败')
        }
    }

    function isOutgoing(message: ChatMessage): boolean {
        if (!account?.wxid) return false
        if (message.fromWxid) return message.fromWxid === account.wxid
        if (message.toWxid) return message.toWxid !== account.wxid
        // Fallback to the stored direction only when wxid fields are absent.
        return message.direction === 'out'
    }

    function participantContact(wxid: string): Contact | undefined {
        return contactMap.get(wxid) || groupMemberMap.get(wxid)
    }

    function messageAvatar(message: ChatMessage) {
        if (isOutgoing(message)) {
            return avatarDisplaySrc(account?.avatar, account?.wxid || 'bot')
        }
        const wxid = message.groupSenderWxid || message.fromWxid || message.conversationWxid
        return avatarDisplaySrc(participantContact(wxid)?.avatar, wxid || message.conversationWxid)
    }

    function messageSenderName(message: ChatMessage) {
        if (isOutgoing(message)) {
            return account?.nickName || account?.wxid || '我'
        }
        const wxid = message.groupSenderWxid || message.fromWxid || message.conversationWxid
        const contact = participantContact(wxid)
        return contact ? displayName(contact) : (wxid === message.conversationWxid ? selectedName : wxid)
    }

    function groupMessageSenderWxid(message: ChatMessage) {
        if (!isGroup || isOutgoing(message)) return ''
        const wxid = message.groupSenderWxid || message.fromWxid || ''
        if (!wxid || wxid === activeWxid || wxid === account?.wxid) return ''
        return wxid
    }

    function groupMessageSenderName(message: ChatMessage) {
        const wxid = groupMessageSenderWxid(message)
        if (!wxid) return ''
        const contact = participantContact(wxid)
        return contact ? displayName(contact) : wxid
    }

    async function handlePinClick() {
        if (!account || !activeWxid) return
        try {
            const response = await apiPost<{success: boolean; pinned: boolean; message?: string}>(
                '/conversations/pin/toggle',
                {wxid: account.wxid, conversationWxid: activeWxid},
            )
            if (response.success) {
                const pinnedAt = response.pinned ? Date.now() : 0
                setIsPinned(response.pinned)
                window.dispatchEvent(new CustomEvent(CONVERSATION_PIN_CHANGE_EVENT, {
                    detail: {
                        accountWxid: account.wxid,
                        conversationWxid: activeWxid,
                        pinned: response.pinned,
                        pinnedAt,
                    },
                }))
            } else {
                message.error(response.message || '操作失败')
            }
        } catch (error) {
            message.error(error instanceof Error ? error.message : '操作失败')
        }
    }

    async function handleAutoGrabRedPacketToggle() {
        if (!account || !activeWxid || autoGrabRedPacketLoading) return
        const enabled = !autoGrabRedPacket
        setAutoGrabRedPacketLoading(true)
        try {
            const response = await apiPost<{success: boolean; enabled: boolean}>(
                '/messages/red-packet/auto-grab',
                {wxid: account.wxid, conversationWxid: activeWxid, enabled},
            )
            setAutoGrabRedPacket(response.enabled === true)
            message.success(response.enabled ? '已开启自动抢红包' : '已关闭自动抢红包')
        } catch (error) {
            message.error(error instanceof Error ? error.message : '操作失败')
        } finally {
            setAutoGrabRedPacketLoading(false)
        }
    }

    function handleDeleteClick() {
        if (!account || !activeWxid) return
        setDeleteConfirmOpen(true)
    }

    async function doDeleteConversation() {
        if (!account || !activeWxid) return
        setDeleteConfirmOpen(false)
        try {
            const response = await apiDelete<{success: boolean; message?: string}>(
                '/conversations?wxid=' +
                    encodeURIComponent(account.wxid) +
                    '&conversationWxid=' +
                    encodeURIComponent(activeWxid),
            )
            if (response.success) {
                message.success('已删除会话')
                router.push('/conversation/realtime')
            } else {
                message.error(response.message || '删除失败')
            }
        } catch (error) {
            message.error(error instanceof Error ? error.message : '删除失败')
        }
    }

    async function handleTranscribe(msg: ChatMessage) {
        if (msg.id <= 0) {
          message.warning('消息仍在发送中，稍后重试')
            return
        }
        if (convertedTexts[msg.id]) return
        if (transcribingIds.has(msg.id)) return
        // Drive a spinner ring on the bubble while the embedded Python
        // pipeline runs. First-call cold-start on Faster-Whisper can take
        // a few seconds, and we don't want the bubble to look frozen.
        setTranscribingIds((prev) => {
            const next = new Set(prev)
            next.add(msg.id)
            return next
        })
        try {
            const response = await apiPost<{success: boolean; text?: string; msg?: string}>(
                '/messages/voice/transcribe',
                {id: msg.id},
            )
            if (!response.success || !response.text) {
                throw new Error(response.msg || '转写失败')
            }
            setConvertedTexts((prev) => ({...prev, [msg.id]: response.text!}))
        } catch (error) {
          message.error(error instanceof Error ? error.message : '语音转文字失败')
        } finally {
            setTranscribingIds((prev) => {
                if (!prev.has(msg.id)) return prev
                const next = new Set(prev)
                next.delete(msg.id)
                return next
            })
        }
    }

    function handleCancelTranscribe(message: ChatMessage) {
        setConvertedTexts((prev) => {
            if (!prev[message.id]) return prev
            const next = {...prev}
            delete next[message.id]
            return next
        })
    }

    async function downloadMessage(msg: ChatMessage) {
        const kind = messageKind(msg)
        let url: string
        let filename: string
        if (kind === 'voice') {
            url = `/api/messages/voice?id=${msg.id}`
            filename = `voice-${msg.id}.wav`
        } else if (kind === 'image') {
            if (!account) return
            try {
                const blob = await apiBlob(`/messages/image?id=${msg.id}&wxid=${encodeURIComponent(account.wxid)}`)
                url = URL.createObjectURL(blob)
                filename = `image-${msg.id}.jpg`
            } catch {
                return
            }
        } else if (kind === 'emoji') {
            if (!account) return
            try {
                const blob = await apiBlob(`/messages/emoji?id=${msg.id}&wxid=${encodeURIComponent(account.wxid)}`)
                url = URL.createObjectURL(blob)
                filename = `emoji-${msg.id}.gif`
            } catch {
                return
            }
        } else if (kind === 'video') {
            if (!account) return
            try {
                const blob = await apiBlob(`/messages/video?id=${msg.id}&wxid=${encodeURIComponent(account.wxid)}`)
                url = URL.createObjectURL(blob)
                filename = `video-${msg.id}.mp4`
            } catch {
                return
            }
        } else if (kind === 'file') {
            if (!account) return
            try {
                const blob = await apiBlob(`/messages/file?id=${msg.id}&wxid=${encodeURIComponent(account.wxid)}`)
                url = URL.createObjectURL(blob)
                filename = messageTitle(msg, `file-${msg.id}`)
            } catch {
                return
            }
        } else {
            message.warning('该消息不支持下载')
            return
        }
        if (msg.id <= 0) {
            message.warning('消息仍在发送中，稍后重试')
            return
        }
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = filename
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        if (url.startsWith('blob:')) URL.revokeObjectURL(url)
        message.success('已开始下载')
    }

    function renderBubble(message: ChatMessage) {
        const kind = messageKind(message)
        if (kind === 'red-packet') {
            const opening = openingRedPacketIds.has(message.id)
            const receipt = message.redPacketReceipt
            const opened = Boolean(receipt) || openedRedPacketIds.has(message.id)
            const receiptAmount = receipt ? `${(receipt.amount / 100).toFixed(2)} 元` : ''
            const wishing = message.redPacketWishing || receipt?.wishing || '微信红包'
            const title = messageTitle(message, '微信红包')
            const status = opening
                ? '正在抢红包...'
                : receipt?.source === 'auto'
                    ? `红包已自动领取${receiptAmount ? ` · ${receiptAmount}` : ''}`
                    : opened
                        ? '红包已领取 · 点击查看'
                        : '点击抢红包'
            return (
                <button
                    type="button"
                    disabled={opening || message.id <= 0}
                    onClick={async () => {
                        setOpeningRedPacketIds((current) => new Set(current).add(message.id))
                        try {
                            const wxid = encodeURIComponent(account?.wxid || '')
                            let response = await apiGet<{detail: unknown}>(`/messages/red-packet/detail?wxid=${wxid}&id=${message.id}`)
                            if (!opened && !redPacketAlreadyOpened(response.detail)) {
                                await apiPost(`/messages/red-packet/open?wxid=${wxid}`, {id: message.id})
                                setOpenedRedPacketIds((current) => new Set(current).add(message.id))
                                toast.success('红包已抢到')
                                response = await apiGet<{detail: unknown}>(`/messages/red-packet/detail?wxid=${wxid}&id=${message.id}`)
                            } else {
                                setOpenedRedPacketIds((current) => new Set(current).add(message.id))
                            }
                            setRedPacketDetail(response.detail)
                        } finally {
                            setOpeningRedPacketIds((current) => {
                                const next = new Set(current)
                                next.delete(message.id)
                                return next
                            })
                        }
                    }}
                    onContextMenu={(event) => openContextMenu(event, message)}
                    className="w-64 overflow-hidden rounded-md bg-[#fa9d3b] text-left text-white shadow-sm transition-colors hover:bg-[#f4932f] disabled:cursor-wait disabled:opacity-70"
                    title={opened ? '点击查看红包' : '点击抢红包'}
                >
                    <div className="flex items-center gap-3 px-4 py-3.5">
                        <Gift size={34} className="shrink-0 text-[#fff4dc]"/>
                        <div className="min-w-0">
                            <div className="truncate text-sm font-medium">{title}</div>
                            <div className="mt-1 text-xs text-[#fff4dc]">{status}</div>
                        </div>
                    </div>
                    <div className="truncate bg-white px-4 py-1.5 text-[11px] text-muted">{wishing}</div>
                </button>
            )
        }
        if (kind === 'voice') {
            return (
                <VoiceMessageBubble
                    message={message}
                    accountWxid={account?.wxid || ''}
                    direction={isOutgoing(message) ? 'out' : 'in'}
                    transcript={convertedTexts[message.id]}
                    transcribing={transcribingIds.has(message.id)}
                    onContextMenu={openContextMenu}
                />
            )
        }
        if (kind === 'video') {
            // Show the video itself in the bubble with `preload="metadata"` so
            // the browser pulls just enough bytes (moov atom) to render the
            // first frame as a poster. No custom controls on the inline
            // element — clicking opens the full VideoPlayerOverlay which
            // handles playback + download.
            const seconds = message.videoPlayLength || 0
            const durationLabel = seconds > 0 ? formatDuration(seconds) : ''
            return (
                <div
                    className={`relative group select-none ${message.status === 'pending' ? 'opacity-70' : ''}`}
                    onContextMenu={(event) => openContextMenu(event, message)}
                    title="点击播放 / 右键可操作"
                >
                    <div
                        className="relative max-w-[240px] max-h-[320px] overflow-hidden rounded border border-hair bg-black cursor-pointer"
                        onClick={() => {
                            if (!message.id) return
                        }}
                    >
                        <LazyRender placeholder={<div className="h-[140px] w-[200px] bg-black"/>}>
                            <VideoMessageVideo
                                messageId={message.id}
                                accountWxid={account?.wxid || ''}
                                localSrc={message.localPreviewUrl || ''}
                                onPreview={(src) => setVideoPreview({src, id: message.id})}
                            />
                        </LazyRender>
                        {/* Center play button — circle + triangle overlay. */}
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm group-hover:bg-black/60 transition-colors">
                                <svg width="22" height="24" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="translate-x-[2px]">
                                    <path d="M2 2 L18 11 L2 20 Z" fill="white"/>
                                </svg>
                            </div>
                        </div>
                        {durationLabel ? (
                            <div className="pointer-events-none absolute bottom-1.5 right-1.5 text-[11px] text-white bg-black/60 px-1.5 py-0.5 rounded">
                                {durationLabel}
                            </div>
                        ) : null}
                    </div>
                    {message.status === 'pending' ? (
                        <div className="absolute bottom-1 left-1 text-[10px] text-white bg-black/50 px-1.5 rounded">发送中</div>
                    ) : null}
                    {message.status === 'failed' ? (
                        <div className="absolute bottom-1 left-1 text-[10px] text-white bg-red-500/90 px-1.5 rounded">发送失败</div>
                    ) : null}
                </div>
            )
        }
        if (kind === 'image') {
            const localSrc = message.localPreviewUrl || ''
            return (
                <div
                    className={`relative group ${message.status === 'pending' ? 'opacity-70' : ''}`}
                    onContextMenu={(event) => openContextMenu(event, message)}
                    title="点击查看原图 / 右键可操作"
                >
                    <LazyRender placeholder={<div className="h-28 w-40 rounded-md bg-gray-100"/>}>
                        <ImageMessageImage
                            messageId={message.id}
                            accountWxid={account?.wxid || ''}
                            localSrc={localSrc}
                            onPreview={(src) => setPreviewState({thumb: src, hd: ''})}
                        />
                    </LazyRender>
                    {/* Hover hint — absolute overlay at the bottom so it
                        doesn't steal real estate when the user isn't
                        hovering. Pointer-events-none so it never blocks
                        the click target. */}
                    {(message.id > 0 || localSrc) && message.status !== 'pending' && message.status !== 'failed' ? (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b flex justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 text-white text-[11px] py-1">
                            点击查看原图
                        </div>
                    ) : null}
                    {message.status === 'pending' ? (
                        <div className="absolute bottom-1 right-1 text-[10px] text-white bg-black/50 px-1.5 rounded">发送中</div>
                    ) : null}
                    {message.status === 'failed' ? (
                        <div className="absolute bottom-1 right-1 text-[10px] text-white bg-red-500/90 px-1.5 rounded">发送失败</div>
                    ) : null}
                </div>
            )
        }
        if (kind === 'emoji') {
            return (
                <div
                    className={`relative group ${message.status === 'pending' ? 'opacity-70' : ''}`}
                    onContextMenu={(event) => openContextMenu(event, message)}
                    title="表情消息 / 右键可操作"
                >
                    <LazyRender placeholder={<div className="h-20 w-20 rounded-md bg-gray-100"/>}>
                        <EmojiMessageImage messageId={message.id} accountWxid={account?.wxid || ''}/>
                    </LazyRender>
                </div>
            )
        }
        if (kind === 'file') {
            const downloadable = message.id > 0 && Boolean(account?.wxid)
            const title = messageTitle(message, '文件')
            const hasCached = !!message.localFilePath
            return (
                <div
                    className={`flex items-center bg-white border border-hair rounded-md p-3 w-64 ${
                        message.status === 'pending' ? 'opacity-70' : ''
                    } ${downloadable ? 'cursor-pointer hover:bg-page' : ''}`}
                    onContextMenu={(event) => openContextMenu(event, message)}
                    onClick={() => {
                        if (downloadable) void downloadMessage(message)
                    }}
                    title={downloadable ? (hasCached ? '已下载，点击打开' : '点击下载') : '发送中'}
                >
                    <div className="flex-1 overflow-hidden pr-3">
                        <div className="text-sm text-ink truncate mb-1">{title}</div>
                        <div className="text-xs text-muted">
                            {message.status === 'pending'
                                ? '正在发送…'
                                : message.status === 'failed'
                                  ? '发送失败'
                                  : hasCached
                                    ? '已下载 · 点击打开'
                                    : '点击下载'}
                        </div>
                    </div>
                    <FileText size={32} className="text-muted flex-shrink-0"/>
                </div>
            )
        }
        return (
            <div
                className={`text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                    message.status === 'pending' ? 'opacity-70' : ''
                } ${message.status === 'failed' ? 'text-red-500' : ''}`}
                onContextMenu={(event) => openContextMenu(event, message)}
            >
                {replaceWeChatEmoticons(voipCallLabel(message) || message.content)}
            </div>
        )
    }

    const menuMessage = contextMenu.message
    const menuKind = menuMessage ? messageKind(menuMessage) : 'text'
    const menuVoiceHasTranscript = menuKind === 'voice' && menuMessage ? Boolean(convertedTexts[menuMessage.id]) : false

    if (!activeWxid) {
        return (
            <section className="flex min-h-0 flex-col h-full bg-page">
                <div className="flex-1 flex items-center justify-center text-muted text-sm">未选择聊天</div>
            </section>
        )
    }

    return (
        <section className="relative flex min-h-0 flex-col h-full bg-page">
            {/* AI 爬楼总结弹窗 */}
            {showSummaryModal && (
                <div
                    className="absolute inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
                    onClick={() => setShowSummaryModal(false)}
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden w-full max-w-[480px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 flex justify-between items-center text-white">
                            <span className="font-medium flex items-center">
                                <Sparkles size={18} className="mr-2"/>
                                群聊简报 (由 AI 自动生成)
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowSummaryModal(false)}
                                className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-md transition-colors"
                            >
                                <X size={16}/>
                            </button>
                        </div>
                        {/* Time range selector */}
                        <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-gray-100">
                            <span className="text-sm text-gray-600">选择总结范围：</span>
                            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                                {([['today', '今日'], ['yesterday', '昨日'], ['3days', '近3天']] as const).map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => { if (key !== summaryRange) handleAiSummary(key) }}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                            summaryRange === key
                                                ? 'bg-white text-purple-600 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="p-6 max-h-[60vh] overflow-y-auto text-sm text-gray-700">
                            {summaryLoading ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-3">
                                    <Sparkles size={32} className="text-purple-400 animate-pulse"/>
                                    <span className="text-gray-500">AI 正在分析群聊记录...</span>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-700 leading-relaxed space-y-4 [&_h1]:text-lg [&_h1]:font-bold [&_h1]:text-gray-800 [&_h1]:border-l-4 [&_h1]:border-indigo-500 [&_h1]:pl-3 [&_h1]:mb-3 [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-gray-800 [&_h2]:border-l-4 [&_h2]:border-indigo-500 [&_h2]:pl-3 [&_h2]:mb-3 [&_h3]:text-sm [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:border-l-4 [&_h3]:border-blue-500 [&_h3]:pl-3 [&_h3]:mb-2 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-purple-800 [&_h4]:mb-2 [&_p]:mb-2 [&_p]:text-gray-600 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 [&_ol]:space-y-2 [&_li]:text-gray-600 [&_li]:leading-relaxed [&_strong]:font-semibold [&_strong]:text-gray-800 [&_a]:text-blue-600 [&_a]:font-medium [&_blockquote]:bg-purple-50 [&_blockquote]:border-l-4 [&_blockquote]:border-purple-200 [&_blockquote]:rounded-lg [&_blockquote]:p-4 [&_blockquote]:my-3 [&_blockquote]:text-purple-700 [&_blockquote_p]:text-purple-700 [&_blockquote_p]:mb-0 [&_code]:bg-blue-50 [&_code]:text-blue-700 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-medium [&_hr]:border-gray-200 [&_hr]:my-4">
                                    <Markdown>{summaryContent}</Markdown>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setShowSummaryModal(false)}
                                className="px-5 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                关闭
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 联系人头像大图预览 */}
            {contactInfoPreviewOpen && selectedContact && (
                <ImagePreviewOverlay
                    src={avatarDisplaySrc(selectedContact.avatar, selectedContact.wxid)}
                    onClose={() => setContactInfoPreviewOpen(false)}
                />
            )}

            {memberAvatarPreview ? (
                <ImagePreviewOverlay
                    src={memberAvatarPreview}
                    onClose={() => setMemberAvatarPreview('')}
                />
            ) : null}

            {momentPreviewImage ? (
                <ImagePreviewOverlay
                    src={momentPreviewImage}
                    onClose={() => setMomentPreviewImage('')}
                />
            ) : null}

            {/* 主内容区 + 联系人信息侧边栏 */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <div className="flex-1 flex flex-col min-w-0">
                    {/* 聊天头部 */}
                    <header className="h-[60px] px-3 md:px-6 border-b border-hair bg-page flex items-center justify-between shrink-0 relative">
                        <div className="flex min-w-0 items-center gap-2">
                            <button type="button" className="icon-btn md:hidden" onClick={() => router.push('/conversation/realtime')} aria-label="返回聊天列表"><ArrowLeft size={20}/></button>
                            <div className="flex min-w-0 flex-col">
                            <strong className="text-base font-medium text-ink truncate flex items-center gap-1.5">
                                <span className="truncate">{selectedName}</span>
                                <SexIcon sex={selectedContact?.sex}/>
                            </strong>
                            <span className="text-xs text-muted truncate">{activeWxid}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 md:gap-3">
                            {/* AI 爬楼总结按钮 - 仅群聊显示 */}
                            {isGroup && (
                                <button
                                    type="button"
                                    onClick={() => handleAiSummary('today')}
                                    className="hidden items-center text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-3 py-1.5 rounded-full hover:shadow-md hover:opacity-90 transition-all font-medium shadow-sm md:flex"
                                >
                                    <Sparkles size={14} className="mr-1.5"/> AI 爬楼总结
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    const next = !showContactInfoSidebar
                                    if (next) setSidebarView('profile')
                                    setShowContactInfoSidebar(next)
                                    try { localStorage.setItem('contactInfoSidebarOpen', String(next)) } catch { /* ignore */ }
                                }}
                                title={showContactInfoSidebar ? '收起联系人信息' : '展开联系人信息'}
                                className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${showContactInfoSidebar ? 'text-[#07c160]' : 'text-gray-500 hover:text-[#07c160]'}`}
                            >
                                {showContactInfoSidebar ? <PanelRightClose size={20}/> : <PanelRightOpen size={20}/>}
                            </button>
                        </div>
                    </header>
                    {/* 消息列表区 */}
            <div ref={historyRef} className="flex-1 overflow-y-auto p-3 md:p-6 space-y-4 md:space-y-6">
                {loadingOlder && (
                    <div className="flex justify-center py-3">
                        <span className="text-xs text-muted">加载更多消息...</span>
                    </div>
                )}
                {!hasMoreOlder && messages.length > 0 && (
                    <div className="flex justify-center py-3">
                        <span className="text-xs text-muted">已加载全部消息</span>
                    </div>
                )}
                {messages.length ? (
                    messages.map((message, index) => {
                        if (isRejectedNoticeMessage(message)) {
                            return null
                        }
                        if (isEmptySystemNotice(message)) {
                            return null
                        }
                        if (isSystemNoticeMessage(message)) {
                            return (
                                <div key={message.id} className="flex w-full justify-center">
                                    <div
                                        className="max-w-[80%] rounded-lg bg-[rgb(229_231_235_/_0.6)] px-4 py-1.5 text-center text-xs leading-relaxed text-muted whitespace-pre-wrap break-words"
                                        title={formatMessageTime(message.createdAt)}
                                    >
                                        {replaceWeChatEmoticons(message.content)}
                                    </div>
                                </div>
                            )
                        }
                        const outgoing = isOutgoing(message)
                        const rejectedNotice = messages[index + 1]
                        const rejectedText = rejectedNotice && isRejectedNoticeMessage(rejectedNotice)
                            ? rejectedNotice.content
                            : ''
                        const kind = messageKind(message)
                        const transparent = kind === 'voice' || kind === 'image' || kind === 'emoji' || kind === 'file' || kind === 'video' || kind === 'red-packet'
                        const groupSenderName = groupMessageSenderName(message)
                        const bubbleCls = transparent
                            ? 'bg-transparent shadow-none p-0'
                            : outgoing
                              // 水平 px-3、垂直 py-2，保证多行文字与卡片之间有
                              // 上下间距；min-w-10 让单字气泡也是矩形。多行内容
                              // 由 renderBubble 里的 whitespace-pre-wrap 撑开。
                              ? 'bg-bubble-out text-ink rounded-lg shadow-sm px-3 py-2 min-w-10'
                              : 'bg-white border border-bubble-inBorder text-ink rounded-lg shadow-sm px-3 py-2 min-w-10'
                        return (
                            <div key={message.id} className="space-y-2">
                                <div className="flex w-full justify-center">
                                    <span className="text-[11px] text-muted">
                                        {formatMessageTime(message.createdAt)}
                                    </span>
                                </div>
                                <div
                                    className={`flex w-full items-start ${outgoing ? 'justify-end' : 'justify-start'}`}
                                >
                                    {!outgoing ? (
                                        <button
                                            type="button"
                                            className="shrink-0 mr-3"
                                            aria-label="查看资料"
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
                                                setAvatarPopover({rect, message})
                                                // Resolve the sender if they're not in
                                                // the local contacts cache yet. The
                                                // popover renders a raw wxid as a
                                                // fallback; contacts update → popover
                                                // re-renders with the real data.
                                                const fromWxid = message.groupSenderWxid || message.fromWxid || message.conversationWxid
                                                if (fromWxid && activeWxid.endsWith('@chatroom')) {
                                                    ensureGroupMemberFriendStatus(fromWxid)
                                                }
                                                if (fromWxid && !activeWxid.endsWith('@chatroom') && !contactMap.has(fromWxid)) {
                                                    void resolveContact(fromWxid)
                                                }
                                            }}
                                        >
                                            <img
                                                src={messageAvatar(message)}
                                                alt=""
                                                className="w-10 h-10 rounded bg-gray-200 flex-shrink-0"
                                            />
                                        </button>
                                    ) : null}
                                    {outgoing && rejectedText ? (
                                        <div
                                            className="mr-2 mt-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500 text-xs font-bold leading-none text-white shadow-[0_0_0_2px_rgba(255,255,255,0.95)]"
                                            title="消息被拒收"
                                        >
                                            !
                                        </div>
                                    ) : null}
                                    <div
                                        className={`flex flex-col ${outgoing ? 'items-end' : 'items-start'} max-w-[78%] md:max-w-[50%]`}
                                    >
                                        {groupSenderName ? (
                                            <div
                                                className="mb-1 max-w-full truncate text-xs leading-4 text-muted"
                                                title={groupSenderName}
                                            >
                                                {groupSenderName}
                                            </div>
                                        ) : null}
                                        <div className={`relative ${bubbleCls} ${outgoing ? 'self-end' : 'self-start'}`}>
                                            {renderBubble(message)}
                                        </div>
                                        {isRecalledMessage(message) ? (
                                            <div
                                                className={`mt-2 flex items-center gap-1.5 text-xs font-medium text-gray-400 ${
                                                    outgoing ? 'self-end' : 'self-start'
                                                }`}
                                            >
                                                <span className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[11px] leading-none">
                                                    i
                                                </span>
                                                <span>{outgoing ? '你' : '对方'}尝试撤回，已被系统拦截</span>
                                            </div>
                                        ) : null}
                                    </div>
                                    {outgoing ? (
                                        <button
                                            type="button"
                                            className="shrink-0 ml-3"
                                            aria-label="查看资料"
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
                                                setAvatarPopover({rect, message})
                                            }}
                                        >
                                            <img
                                                src={messageAvatar(message)}
                                                alt=""
                                                className="w-10 h-10 rounded bg-gray-200 flex-shrink-0"
                                            />
                                        </button>
                                    ) : null}
                                </div>
                                {isRecalledMessage(message) ? (
                                    <div className="flex w-full justify-center">
                                        <div className="max-w-[80%] rounded-lg bg-[rgb(229_231_235_/_0.6)] px-5 py-2 text-center text-sm text-gray-500">
                                            &quot;{messageSenderName(message)}&quot; 撤回了一条消息（已被自动拦截）
                                        </div>
                                    </div>
                                ) : null}
                                {rejectedText ? (
                                    <div className="flex w-full justify-center">
                                        <div className="max-w-[80%] rounded-lg bg-[rgb(229_231_235_/_0.6)] px-5 py-2 text-center text-sm text-gray-500">
                                            {rejectedText}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )
                    })
                ) : (
                    <div className="h-full flex items-center justify-center text-muted text-sm">暂无聊天记录</div>
                )}
                {/* Anchor element the auto-scroll effect targets via
                    scrollIntoView. Zero-size so it doesn't affect layout. */}
                <div ref={bottomAnchorRef} aria-hidden="true"/>
            </div>

            {/* 输入区 */}
            <div
                className="relative flex shrink-0 flex-col border-t border-hair bg-white"
                style={{height: effectiveComposerHeight}}
            >
                {/* 高度拖拽手柄 */}
                <div
                    role="separator"
                    aria-orientation="horizontal"
                    className={`absolute -top-0.5 left-0 right-0 h-1 cursor-row-resize z-30 ${
                        resizingComposer ? 'bg-accent/40' : 'hover:bg-accent/30'
                    }`}
                    onMouseDown={(event) => {
                        event.preventDefault()
                        setResizingComposer(true)
                    }}
                    onDoubleClick={() => {
                        composerHeightRef.current = DEFAULT_COMPOSER_HEIGHT
                        setComposerHeight(DEFAULT_COMPOSER_HEIGHT)
                        window.localStorage.setItem(COMPOSER_HEIGHT_STORAGE_KEY, String(DEFAULT_COMPOSER_HEIGHT))
                    }}
                    title="拖动调整高度（双击恢复默认）"
                />
                <div className="flex min-h-0 flex-1 flex-col px-2 py-1.5 md:px-3 md:py-2">
                    <ReplyPayloadEditor
                        payload={composerPayload}
                        onChange={setComposerPayload}
                        onSubmit={() => void handleSendAll()}
                        placeholder="请输入消息... (Enter发送)"
                        voiceId={settings.tts?.selectedVoiceId}
                        mentionOptions={mentionOptions}
                        fill
                        afterQuickReply={
                            <button
                                type="button"
                                onClick={() => void handleGenerateAIReply()}
                                disabled={!account || !activeWxid || aiReplyLoading}
                                className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                                    account && activeWxid && !aiReplyLoading
                                        ? 'hover:bg-page hover:text-indigo-600'
                                        : 'cursor-not-allowed opacity-50'
                                }`}
                                title={`AI 生成回复（参考最近 ${settings.system?.aiReplyHistoryLimit ?? settings.aiConfig?.replyHistoryLimit ?? 50} 条聊天记录）`}
                                aria-label="AI 生成回复"
                            >
                                {aiReplyLoading ? (
                                    <Loader2 size={20} className="animate-spin text-indigo-500"/>
                                ) : (
                                    <Sparkles size={20}/>
                                )}
                            </button>
                        }
                        action={
                            <button
                                type="button"
                                onClick={() => void handleSendAll()}
                                disabled={!canSend}
                                className={`px-6 h-9 rounded text-sm transition-colors font-medium ${
                                    canSend
                                        ? 'bg-accent hover:bg-accent-hover text-white'
                                        : 'bg-[#e9e9e9] text-muted cursor-not-allowed'
                                }`}
                            >
                                {sending ? '发送中' : '发送'}
                            </button>
                        }
                    />
                </div>
            </div>
                </div>

            {/* 联系人信息侧边栏 */}
            {showContactInfoSidebar && activeWxid && (
                <div className="fixed inset-0 z-overlay flex h-full w-full shrink-0 flex-col bg-page md:static md:w-[360px] md:border-l md:border-gray-200">
                    {(canShowMomentsTab || isGroup) ? (
                        <div className="flex h-[60px] border-b border-hair bg-page shrink-0">
                            <button
                                type="button"
                                onClick={() => setSidebarView('profile')}
                                className={`flex-1 h-full text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                                    sidebarView === 'profile' ? 'text-accent border-b-2 border-accent' : 'text-soft hover:text-ink'
                                }`}
                            >
                                <PanelRightOpen size={15}/> 资料
                            </button>
                            {canShowMomentsTab ? (
                                <button
                                    type="button"
                                    onClick={() => setSidebarView('moments')}
                                    className={`flex-1 h-full text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                                        sidebarView === 'moments' ? 'text-accent border-b-2 border-accent' : 'text-soft hover:text-ink'
                                    }`}
                                >
                                    <Aperture size={15}/> 朋友圈
                                </button>
                            ) : null}
                            {isGroup ? (
                                <button
                                    type="button"
                                    onClick={handleShowGroupMembers}
                                    className={`flex-1 h-full text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                                        sidebarView === 'members' ? 'text-accent border-b-2 border-accent' : 'text-soft hover:text-ink'
                                    }`}
                                >
                                    <Users size={15}/> 群成员({groupMembers.length})
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    {sidebarView === 'profile' ? (
                        <div className="flex-1 overflow-y-auto">
                            {/* 头像区域 */}
                            <div className="flex flex-col items-center pt-8 pb-6 px-6">
                                <button
                                    type="button"
                                    className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 shadow-sm border border-gray-200 mb-3"
                                    onClick={() => selectedContact && setContactInfoPreviewOpen(true)}
                                >
                                    <img
                                        src={avatarDisplaySrc(selectedContact?.avatar, activeWxid)}
                                        alt={selectedName}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                                <div className="text-base font-medium text-ink flex items-center gap-1.5">
                                    <span>{selectedName}</span>
                                    <SexIcon sex={selectedContact?.sex}/>
                                </div>
                                {selectedContact?.remark && selectedContact.nickName && selectedContact.remark !== selectedContact.nickName && (
                                    <div className="text-xs text-soft mt-1">昵称：{selectedContact.nickName}</div>
                                )}
                                <div className="text-xs text-muted mt-1">{activeWxid}</div>
                            </div>

                            {/* 信息列表 */}
                            <div className="px-6 pb-6 space-y-4">
                                {/* 备注 */}
                                {selectedContact ? (
                                <div className="flex items-baseline gap-3 text-sm">
                                    <span className="text-muted shrink-0 w-12">备注</span>
	                                    {editingRemark ? (
	                                        <input
	                                            type="text"
	                                            value={remarkValue}
	                                            onChange={(e) => setRemarkValue(e.target.value)}
	                                            className="flex-1 px-1.5 py-0.5 border border-gray-300 rounded text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
	                                            autoFocus
	                                            onKeyDown={(e) => {
	                                                if (e.key === 'Enter') {
	                                                    e.currentTarget.blur()
	                                                }
	                                                if (e.key === 'Escape') {
	                                                    skipRemarkSaveRef.current = true
	                                                    setRemarkValue(selectedContact.remark || '')
	                                                    e.currentTarget.blur()
	                                                    setEditingRemark(false)
	                                                }
	                                            }}
	                                            onBlur={commitRemark}
	                                        />
	                                    ) : (
	                                        <span className="text-ink break-all cursor-pointer hover:text-accent" onClick={() => { setRemarkValue(selectedContact.remark || ''); setEditingRemark(true) }}>
	                                            {remarkValue || '点击设置备注'}
	                                        </span>
	                                    )}
                                </div>
                                ) : null}

                                {selectedContact?.alias && (
                                    <div className="flex items-baseline gap-3 text-sm">
                                        <span className="text-muted shrink-0 w-12">微信号</span>
                                        <span className="text-ink break-all">{selectedContact.alias}</span>
                                    </div>
                                )}
                                {selectedContact?.nickName && (
                                    <div className="flex items-baseline gap-3 text-sm">
                                        <span className="text-muted shrink-0 w-12">昵称</span>
                                        <span className="text-ink break-all">{selectedContact.nickName}</span>
                                    </div>
                                )}
                                {(selectedContact?.province || selectedContact?.city) && (
                                    <div className="flex items-baseline gap-3 text-sm">
                                        <span className="text-muted shrink-0 w-12">地区</span>
                                        <span className="text-ink break-all">
                                            {[selectedContact.province, selectedContact.city].filter(Boolean).join(' ')}
                                        </span>
                                    </div>
                                )}
                                {selectedContact?.signature && (
                                    <div className="flex items-baseline gap-3 text-sm">
                                        <span className="text-muted shrink-0 w-12">签名</span>
                                        <span className="text-ink break-all leading-relaxed">{selectedContact.signature}</span>
                                    </div>
                                )}

                                {/* 标签 */}
                                {selectedContact && !isGroup ? (
                                <div className="text-sm">
                                    <span className="text-muted block mb-2">标签</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {sidebarLabels.map((label) => {
                                            const selected = parseContactLabelIds(selectedContact.labelIds).includes(label.id)
                                            return (
                                                <button
                                                    key={label.id}
                                                    type="button"
                                                    onClick={() => handleContactLabelToggle(label.id, selected)}
                                                    className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${selected ? 'bg-accent text-white border-accent' : 'bg-gray-50 text-muted border-gray-200 hover:border-accent/50'}`}
                                                >
                                                    {label.name}
                                                </button>
                                            )
                                        })}
                                        {sidebarLabels.length === 0 && <span className="text-xs text-muted">暂无标签</span>}
                                    </div>
                                </div>
                                ) : null}

                                {/* 分隔线 */}
                                <div className="border-t border-hair"/>

                                {/* 快捷操作 */}
                                <div className="space-y-3">
                                    {/* 自动抢红包 */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-ink">自动抢红包</span>
                                        <button
                                            type="button"
                                            onClick={() => void handleAutoGrabRedPacketToggle()}
                                            className={`w-9 h-5 rounded-full transition-colors relative ${autoGrabRedPacket ? 'bg-accent' : 'bg-gray-300'}`}
                                        >
                                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoGrabRedPacket ? 'left-[18px]' : 'left-0.5'}`}/>
                                        </button>
                                    </div>

                                    {/* 置顶聊天 */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-ink">置顶聊天</span>
                                        <button
                                            type="button"
                                            onClick={() => void handlePinClick()}
                                            className={`w-9 h-5 rounded-full transition-colors relative ${isPinned ? 'bg-accent' : 'bg-gray-300'}`}
                                        >
                                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${isPinned ? 'left-[18px]' : 'left-0.5'}`}/>
                                        </button>
                                    </div>

                                    {/* 免打扰 */}
                                    {selectedContact ? (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-ink">免打扰</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const next = !contactMuted
                                                setContactMuted(next)
                                                apiPost('/contacts/muted', {wxid: account?.wxid, targetWxid: selectedContact.wxid, muted: next ? 1 : 0}, {silent: true}).catch(() => {})
                                            }}
                                            className={`w-9 h-5 rounded-full transition-colors relative ${contactMuted ? 'bg-accent' : 'bg-gray-300'}`}
                                        >
                                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${contactMuted ? 'left-[18px]' : 'left-0.5'}`}/>
                                        </button>
                                    </div>
                                    ) : null}
                                </div>

                                {/* 分隔线 */}
                                <div className="border-t border-hair"/>

                                {/* 聊天笔记 */}
                                <div className="text-sm">
                                    <span className="text-muted block mb-2">聊天笔记</span>
                                    <textarea
                                        value={chatNoteValue}
                                        onChange={(e) => setChatNoteValue(e.target.value)}
                                        onBlur={() => apiPost('/conversations/note', {wxid: account?.wxid, conversationWxid: activeWxid, chatNote: chatNoteValue}, {silent: true}).catch(() => {})}
                                        placeholder="记录一些关于这段聊天的笔记..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent min-h-[80px]"
                                    />
                                </div>
                            </div>
                        </div>
                    ) : sidebarView === 'moments' ? (
                        <div
                            ref={momentsScrollRef}
                            className="flex-1 overflow-y-auto bg-[#f7f7f7] overscroll-y-contain"
                            onWheel={(event) => {
                                if (event.deltaY < -40 && momentsScrollRef.current?.scrollTop === 0) {
                                    triggerSidebarPullRefresh()
                                }
                            }}
                            onTouchStart={(event) => {
                                if (momentsScrollRef.current?.scrollTop === 0) {
                                    momentsTouchStartYRef.current = event.touches[0]?.clientY ?? null
                                }
                            }}
                            onTouchMove={(event) => {
                                const startY = momentsTouchStartYRef.current
                                if (startY === null) return
                                const delta = (event.touches[0]?.clientY ?? startY) - startY
                                setSidebarPulling(delta > 28)
                            }}
                            onTouchEnd={() => {
                                if (sidebarPulling) triggerSidebarPullRefresh()
                                setSidebarPulling(false)
                                momentsTouchStartYRef.current = null
                            }}
                        >
                            {sidebarMomentsLoading ? (
                                <div className="flex justify-center py-16 text-muted">
                                    <Loader2 size={22} className="animate-spin"/>
                                </div>
                            ) : sidebarMoments.length === 0 ? (
                                <div className="px-6 py-16 text-center text-sm text-muted">
                                    暂无朋友圈缓存
                                </div>
                            ) : (
                                <div className="space-y-3 p-3">
                                    {sidebarMoments.map((moment) => {
                                        const contact = contactMap.get(moment.authorWxid)
                                        const author = contact ? displayName(contact) : moment.authorName || moment.authorWxid || '微信用户'
                                        const images = moment.media.filter((media) => media.type === 'image')
                                        const videos = moment.media.filter((media) => media.type === 'video')
                                        return (
                                            <article key={moment.momentId} className="rounded-md border border-hair bg-white p-3">
                                                <div className="flex gap-3">
                                                    <img
                                                        src={avatarDisplaySrc(contact?.avatar, moment.authorWxid || author)}
                                                        alt=""
                                                        className="h-9 w-9 shrink-0 rounded bg-gray-100"
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="truncate text-sm font-medium text-[#576b95]">{author}</div>
                                                        {moment.content ? (
                                                            <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-ink">
                                                                {moment.content}
                                                            </p>
                                                        ) : null}
                                                        {images.length > 0 ? (
                                                            <div className="mt-2 grid grid-cols-3 gap-1">
                                                                {images.slice(0, 9).map((media, index) => {
                                                                    const thumb = momentMediaUrl(account?.wxid || '', media.thumbUrl || media.url, media.thumbKey || media.key)
                                                                    const full = momentMediaUrl(account?.wxid || '', media.url || media.thumbUrl, media.key || media.thumbKey)
                                                                    return (
                                                                        <button
                                                                            key={`${media.url}-${index}`}
                                                                            type="button"
                                                                            onClick={() => setMomentPreviewImage(full)}
                                                                            className="aspect-square overflow-hidden rounded bg-gray-100"
                                                                        >
                                                                            <img src={thumb} alt="" className="h-full w-full object-cover"/>
                                                                        </button>
                                                                    )
                                                                })}
                                                            </div>
                                                        ) : null}
                                                        {videos.map((media, index) => (
                                                            <video
                                                                key={`${media.url}-${index}`}
                                                                src={momentMediaUrl(account?.wxid || '', media.url, media.key)}
                                                                poster={momentMediaUrl(account?.wxid || '', media.thumbUrl, media.thumbKey || media.key)}
                                                                controls
                                                                preload="metadata"
                                                                className="mt-2 max-h-48 w-full rounded bg-black object-contain"
                                                            />
                                                        ))}
                                                        <div className="mt-2 flex items-center justify-between text-xs text-muted">
                                                            <span>{formatMomentTime(moment.createdAt)}</span>
                                                            <span className="flex items-center gap-3">
                                                                <button
                                                                    type="button"
                                                                    disabled={moment.liked || sidebarActingMomentId === moment.momentId}
                                                                    onClick={() => void runSidebarMomentAction(moment, 'like')}
                                                                    className={`flex items-center gap-1 rounded px-1 py-0.5 transition-colors disabled:opacity-60 ${moment.liked ? 'text-red-500' : 'hover:bg-page hover:text-red-500'}`}
                                                                    title={moment.liked ? '已点赞' : '点赞'}
                                                                >
                                                                    <Heart size={12} className={moment.liked ? 'fill-current' : ''}/>
                                                                    {moment.likeCount > 0 ? moment.likeCount : '赞'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    disabled={sidebarActingMomentId === moment.momentId}
                                                                    onClick={() => {
                                                                        setSidebarCommentingMomentId(sidebarCommentingMomentId === moment.momentId ? '' : moment.momentId)
                                                                        setSidebarCommentText('')
                                                                    }}
                                                                    className="flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:bg-page hover:text-accent disabled:opacity-60"
                                                                    title="评论"
                                                                >
                                                                    <MessageSquare size={12}/>
                                                                    {moment.comments.length > 0 ? moment.comments.length : '评论'}
                                                                </button>
                                                            </span>
                                                        </div>
                                                        {sidebarCommentingMomentId === moment.momentId ? (
                                                            <div className="mt-2 flex gap-2">
                                                                <input
                                                                    value={sidebarCommentText}
                                                                    onChange={(event) => setSidebarCommentText(event.target.value)}
                                                                    onKeyDown={(event) => {
                                                                        if (event.key === 'Enter' && sidebarCommentText.trim()) {
                                                                            void runSidebarMomentAction(moment, 'comment', sidebarCommentText.trim())
                                                                        }
                                                                    }}
                                                                    autoFocus
                                                                    placeholder="输入评论"
                                                                    className="h-8 min-w-0 flex-1 rounded border border-hair px-2 text-xs outline-none focus:border-accent"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    disabled={!sidebarCommentText.trim() || sidebarActingMomentId === moment.momentId}
                                                                    onClick={() => void runSidebarMomentAction(moment, 'comment', sidebarCommentText.trim())}
                                                                    className="h-8 rounded bg-accent px-2.5 text-xs text-white disabled:opacity-50"
                                                                >
                                                                    发送
                                                                </button>
                                                            </div>
                                                        ) : null}
                                                        {(moment.likedBy.length > 0 || moment.comments.length > 0) ? (
                                                            <div className="mt-2 rounded bg-page px-2.5 py-2 text-xs leading-relaxed">
                                                                {moment.likedBy.length > 0 ? (
                                                                    <div className={`flex gap-1.5 text-[#576b95] ${moment.comments.length > 0 ? 'border-b border-hair pb-1.5 mb-1.5' : ''}`}>
                                                                        <Heart size={12} className="mt-0.5 shrink-0 fill-red-500 text-red-500"/>
                                                                        <span className="min-w-0 break-words">
                                                                            {moment.likedBy.map((item) => item.author).join('、')}
                                                                        </span>
                                                                    </div>
                                                                ) : null}
                                                                {moment.comments.map((comment, index) => (
                                                                    <div key={`${comment.commentId || index}-${comment.author}`} className="break-words">
                                                                        <span className="font-medium text-[#576b95]">{comment.author}</span>
                                                                        <span className="text-soft">: </span>
                                                                        <span className="text-ink">{comment.content}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </article>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex min-h-0 flex-1 flex-col bg-white">
                            <div className="shrink-0 border-b border-hair bg-white p-3">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 text-gray-400" size={14}/>
                                    <input
                                        type="text"
                                        placeholder="搜索群成员"
                                        value={memberSearchTerm}
                                        onChange={(e) => setMemberSearchTerm(e.target.value)}
                                        className="h-9 w-full rounded-md border border-transparent bg-page pl-8 pr-3 text-sm outline-none transition-colors focus:border-accent"
                                    />
                                </div>
                            </div>
                            <div className="min-h-0 flex-1 overflow-y-auto">
                                {groupMembersLoading ? (
                                    <div className="flex justify-center py-16 text-muted">
                                        <Loader2 size={22} className="animate-spin"/>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-hair/70">
                                        {filteredGroupMembers.map((member) => {
                                            const stat = groupMemberStats[member.wxid]
                                            const status = groupMemberStatus(member)
                                            const actionLabel = groupMemberActionLabel(member)
                                            const actionLoading = addingFriendWxid === member.wxid || status === 'checking'
                                            const memberName = member.remark || member.nickName || member.name || member.wxid
                                            const joinTime = memberJoinTime(member)
                                            return (
                                                <div key={member.wxid} className="group flex items-center gap-3 px-4 py-3 hover:bg-page/70">
                                                    <button
                                                        type="button"
                                                        className="h-11 w-11 shrink-0 overflow-hidden rounded bg-gray-100"
                                                        title="查看头像"
                                                        onClick={() => setMemberAvatarPreview(avatarDisplaySrc(member.avatar, member.wxid))}
                                                    >
                                                        <img
                                                            src={avatarDisplaySrc(member.avatar, member.wxid)}
                                                            alt={memberName}
                                                            className="h-full w-full object-cover"
                                                        />
                                                    </button>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="truncate text-sm font-medium text-ink">{memberName}</span>
                                                            <SexIcon sex={member.sex}/>
                                                        </div>
                                                        <div className="mt-0.5 truncate text-xs text-muted">{member.alias || member.wxid}</div>
                                                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted">
                                                            <span>入群 {joinTime ? formatMemberTime(joinTime) : '-'}</span>
                                                            <span>发言 {stat?.messageCount || 0}</span>
                                                            <span>活跃 {stat?.lastActiveAt ? formatMemberTime(stat.lastActiveAt) : '-'}</span>
                                                        </div>
                                                    </div>
                                                    {member.wxid === account?.wxid ? (
                                                        <span className="h-8 w-8 shrink-0" aria-hidden/>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            disabled={actionLoading}
                                                            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-transparent text-ink transition-all hover:text-accent disabled:opacity-50 ${
                                                                actionLoading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
                                                            }`}
                                                            title={actionLabel}
                                                            aria-label={actionLabel}
                                                            onClick={() => void handleGroupMemberAction(member)}
                                                        >
                                                            {actionLoading ? (
                                                                <Loader2 size={15} className="animate-spin"/>
                                                            ) : status === 'friend' ? (
                                                                <MessageSquare size={15}/>
                                                            ) : (
                                                                <UserPlus size={15}/>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            )
                                        })}
                                        {!filteredGroupMembers.length ? (
                                            <div className="py-12 text-center text-sm text-muted">
                                                {groupMembers.length ? '没有匹配的群成员' : '暂无群成员'}
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                )}
            </div>

            {redPacketDetail ? (
                <div className="absolute inset-0 z-[220] flex items-center justify-center bg-black/40 p-4" onClick={() => setRedPacketDetail(null)}>
                    <div className="w-full max-w-[430px] overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
                        <div className="bg-[#fa9d3b] px-5 py-5 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 font-medium"><Gift size={20}/> 红包详情</div>
                                <button type="button" onClick={() => setRedPacketDetail(null)} className="rounded p-1 text-white/80 hover:bg-white/15 hover:text-white"><X size={17}/></button>
                            </div>
                            <div className="mt-5 text-center">
                                <div className="text-3xl font-semibold">{redPacketMoney(redPacketDetail, 'amount', 'Amount', 'totalAmount', 'TotalAmount')}</div>
                                <div className="mt-2 text-sm text-white/90">{redPacketText(redPacketDetail, 'wishing', 'Wishing') || '微信红包'}</div>
                            </div>
                        </div>
                        <div className="border-b border-hair px-5 py-3 text-sm text-soft">
                            已领取 {redPacketText(redPacketDetail, 'recNum', 'RecNum') || '0'}/{redPacketText(redPacketDetail, 'totalNum', 'TotalNum') || '0'} 个，共 {redPacketMoney(redPacketDetail, 'totalAmount', 'TotalAmount', 'recAmount', 'RecAmount', )}
                        </div>
                        <div className="max-h-[320px] overflow-y-auto px-5 py-2">
                            {redPacketRecords(redPacketDetail).length ? redPacketRecords(redPacketDetail).map((row, index) => {
                                const receiverWxid = redPacketReceiverWxid(row)
                                const receiverContact = participantContact(receiverWxid)
                                const receiverName = receiverWxid === account?.wxid
                                    ? account?.nickName || account.wxid
                                    : receiverContact
                                        ? displayName(receiverContact)
                                        : receiverWxid || '微信用户'
                                return (
                                    <div key={`${redPacketText(row, 'receiveId', 'ReceiveID')}-${index}`} className="flex items-center justify-between border-b border-hair py-3 last:border-b-0">
                                        <div className="flex min-w-0 items-center gap-3">
                                            <img
                                                src={receiverWxid === account?.wxid
                                                    ? avatarDisplaySrc(account?.avatar, account.wxid)
                                                    : avatarDisplaySrc(receiverContact?.avatar, receiverWxid || `red-packet-${index}`)}
                                                alt=""
                                                className="h-9 w-9 shrink-0 rounded object-cover"
                                            />
                                            <div className="min-w-0">
                                                <div className="truncate text-sm text-ink">{receiverName}</div>
                                                <div className="mt-1 text-xs text-muted">{redPacketText(row, 'receiveTime', 'ReceiveTime')}</div>
                                            </div>
                                        </div>
                                        <div className="shrink-0 pl-4 text-sm font-medium text-ink">{redPacketMoney(row, 'receiveAmount', 'ReceiveAmount')}</div>
                                    </div>
                                )
                            }) : (
                                <div className="py-10 text-center text-sm text-muted">{redPacketText(redPacketDetail, 'statusMess', 'StatusMess') || '暂无领取记录'}</div>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            {previewState ? (
                <ImagePreviewOverlay
                    src={previewState.thumb}
                    hdSrc={previewState.hd || undefined}
                    downloadName={previewState.id ? `image-${previewState.id}.jpg` : 'image.jpg'}
                    onHdLoaded={() => {
                        // HD bytes have been decoded in-browser. The server
                        // wrote them to disk and updated `messages.local_file_path`
                        // as part of the same request, so flip the bubble's
                        // `localFilePath` locally to keep the list view in
                        // sync — React will re-render the <img> with the HD
                        // URL, which the browser serves straight from its
                        // HTTP cache.
                        const targetId = previewState.id
                        if (!targetId || targetId <= 0) return
                        setMessages((current) =>
                            current.map((item) =>
                                item.id === targetId && !item.localFilePath
                                    ? {...item, localFilePath: `image-hd/${targetId}`}
                                    : item,
                            ),
                        )
                    }}
                    onClose={() => setPreviewState(null)}
                />
            ) : null}

            {videoPreview ? (
                <VideoPlayerOverlay
                    src={videoPreview.src}
                    downloadName={`video-${videoPreview.id}.mp4`}
                    onClose={() => {
                        // Mark this message as HD-cached so the bubble knows
                        // the video has been pulled at least once. The server
                        // already updated `local_file_path` as part of the
                        // stream request. The real path comes back on the
                        // next listMessages; the placeholder here just keeps
                        // React from re-issuing the fetch.
                        const targetId = videoPreview.id
                        if (targetId > 0) {
                            setMessages((current) =>
                                current.map((item) =>
                                    item.id === targetId && !item.localFilePath
                                        ? {...item, localFilePath: `video/${targetId}`}
                                        : item,
                                ),
                            )
                        }
                        setVideoPreview(null)
                    }}
                />
            ) : null}

            {avatarPopover ? (() => {
                const message = avatarPopover.message
                const outgoing = isOutgoing(message)
                const fromWxid = message.groupSenderWxid || message.fromWxid || message.conversationWxid
                if (outgoing) {
                    const accountWxid = account?.wxid || fromWxid
                    return (
                        <ContactProfilePopover
                            anchorRect={avatarPopover.rect}
                            avatar={avatarDisplaySrc(account?.avatar, accountWxid || 'bot')}
                            latestAvatarUrl={account?.avatar}
                            displayName={account?.nickName || accountWxid}
                            wxid={accountWxid}
                            nickName={account?.nickName}
                            onClose={() => setAvatarPopover(null)}
                        />
                    )
                }
                const contact = participantContact(fromWxid)
                const name = contact ? displayName(contact) : fromWxid
                const avatar = avatarDisplaySrc(contact?.avatar, fromWxid)
                const region = contact?.region || [contact?.province, contact?.city].filter(Boolean).join(' ')
                const groupMemberStatus = isGroup ? groupMemberFriendStatus[fromWxid] : undefined
                const actionContact = contact || ({wxid: fromWxid, name} as Contact)
                return (
                    <ContactProfilePopover
                        anchorRect={avatarPopover.rect}
                        avatar={avatar}
                        latestAvatarUrl={contactLatestAvatarUrl(contact)}
                        displayName={name}
                        wxid={fromWxid}
                        remark={contact?.remark}
                        nickName={contact?.nickName}
                        alias={contact?.alias}
                        region={region || undefined}
                        signature={contact?.signature}
                        sex={contact?.sex}
                        actionLabel={
                            groupMemberStatus === 'friend'
                                ? '发消息'
                                : groupMemberStatus === 'notFriend'
                                  ? '添加好友'
                                  : groupMemberStatus === 'checking'
                                    ? '检测中...'
                                    : undefined
                        }
                        actionLoading={groupMemberStatus === 'checking' || addingFriendWxid === fromWxid}
                        onAction={
                            groupMemberStatus === 'friend'
                                ? () => openDirectMessage(fromWxid)
                                : groupMemberStatus === 'notFriend'
                                  ? () => void handleAddGroupMemberFriend(actionContact)
                                  : undefined
                        }
                        onClose={() => setAvatarPopover(null)}
                    />
                )
            })() : null}

            {contextMenu.visible && menuMessage ? (
                <div
                    className="fixed bg-white border border-hair shadow-card rounded py-1 z-50 w-32 overflow-hidden text-sm"
                    style={{top: contextMenu.y, left: contextMenu.x}}
                    onClick={(event) => event.stopPropagation()}
                >
                    {menuKind === 'voice' ? (
                        <button
                            type="button"
                            onClick={() => {
                                if (menuVoiceHasTranscript) {
                                    handleCancelTranscribe(menuMessage)
                                } else {
                                    handleTranscribe(menuMessage)
                                }
                                setContextMenu({visible: false, x: 0, y: 0, message: null})
                            }}
                            className="w-full flex items-center gap-2 text-left px-4 py-2 text-ink hover:bg-page"
                        >
                            {menuVoiceHasTranscript ? <X size={14}/> : <TextSearch size={14}/>}
                            {menuVoiceHasTranscript ? '取消转文字' : '转文字'}
                        </button>
                    ) : null}
                    {menuKind === 'text' ? (
                        <button
                            type="button"
                            onClick={() => {
                                void copyMessage(menuMessage)
                                setContextMenu({visible: false, x: 0, y: 0, message: null})
                            }}
                            className="w-full flex items-center gap-2 text-left px-4 py-2 text-ink hover:bg-page"
                        >
                            <Copy size={14}/>
                            复制
                        </button>
                    ) : null}
                    {['voice', 'image', 'emoji', 'video', 'file'].includes(menuKind) ? (
                        <button
                            type="button"
                            onClick={() => {
                                downloadMessage(menuMessage)
                                setContextMenu({visible: false, x: 0, y: 0, message: null})
                            }}
                            className="w-full flex items-center gap-2 text-left px-4 py-2 text-ink hover:bg-page"
                        >
                            <Download size={14}/>
                            下载
                        </button>
                    ) : null}
                </div>
            ) : null}

            <ConfirmDialog
                open={deleteConfirmOpen}
                title="删除聊天"
                description="确定删除该会话的所有聊天记录吗？此操作不可撤销。"
                confirmLabel="删除"
                danger
                onConfirm={doDeleteConversation}
                onCancel={() => setDeleteConfirmOpen(false)}
            />
        </section>
    )
}
